import { useState, useEffect, useRef, useMemo } from 'react';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useAuth, PERMISSIONS } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Switch } from '@/components/ui/switch';
import { 
  FileText, Upload, Search, CheckCircle, Clock,
  XCircle, Eye, MoreVertical, Sparkles, ChevronLeft, ChevronRight, Trash2,
  Loader2, AlertCircle, Database, RefreshCw, X, ChevronDown, TrendingUp, Flame, ThumbsUp,
  HelpCircle, MessageSquare, CheckCheck, Edit, Tag, Send, Archive, FileType, File, Info,
  Calendar, User, Hash, Globe, Building, Folder, FolderOpen,
  CloudUpload, FolderInput, Bookmark, Users, Play, Pause, MapPin, Shield, ShieldCheck, Download,
  FileCheck2, SlidersHorizontal
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import api from '@/lib/api';
import { cn, parseErrorDetail, showLimitError } from '@/lib/utils';
import { toast } from 'sonner';
import FolderTree from '@/components/FolderTree';
import FolderMoveDialog from '@/components/FolderMoveDialog';
import DocumentMetadataEditor from '@/components/DocumentMetadataEditor';
import BulkMetadataDialog from '@/components/BulkMetadataDialog';
import UploadDocumentDialog from '@/components/UploadDocumentDialog';
import DocumentWorkflowStepper from '@/components/DocumentWorkflowStepper';
import KbNotIngestedNotice from '@/components/KbNotIngestedNotice';
import { PageBanner } from '@/components/PageBanner';
import { PageHeader } from '@/components/PageHeader';
import { useConfirm } from '@/components/ConfirmDialog';
import { KpiCard } from '@/components/KpiCard';
import { LimitMeter } from '@/components/LimitMeter';
import { DataTablePagination, DataTableSortHeader, DataTableToolbar } from '@/components/DataTableControls';
import { EmptyState } from '@/components/EmptyState';
import { tokenLimitFormatter } from '@/components/LimitMeter';
import { MarkdownContent } from '@/components/MarkdownContent';

// Neutral color scheme for status badges - rely on icons for differentiation
const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-secondary text-muted-foreground dark:bg-card dark:text-muted-foreground', icon: FileText },
  pending_review: { label: 'Pending Review', color: 'bg-secondary text-muted-foreground dark:bg-card dark:text-muted-foreground', icon: Clock },
  approved: { label: 'Approved', color: 'bg-secondary text-muted-foreground dark:bg-card dark:text-muted-foreground', icon: CheckCircle },
  published: { label: 'Published', color: 'bg-secondary text-muted-foreground dark:bg-card dark:text-muted-foreground', icon: CheckCheck },
  archived: { label: 'Archived', color: 'bg-secondary text-muted-foreground dark:bg-card dark:text-muted-foreground', icon: XCircle },
};

// Subtle gray badges for ingestion - icons convey state
// The live sequence is queued -> parsing -> analyzing -> indexed. `chunking`
// and `embedding` are retained for rows written before the ingestion pipeline
// stopped chunking; nothing writes them any more. `complete` is deliberately
// absent: IngestionStatusBadge only renders for status === 'published', and a
// published document is written as 'indexed', never 'complete'.
const INGESTION_CONFIG = {
  queued: { label: 'Queued', color: 'bg-secondary text-muted-foreground dark:bg-card dark:text-muted-foreground', icon: Clock },
  parsing: { label: 'Parsing', color: 'bg-secondary text-muted-foreground dark:bg-card dark:text-muted-foreground', icon: Loader2, animate: true },
  analyzing: { label: 'Analyzing', color: 'bg-secondary text-muted-foreground dark:bg-card dark:text-muted-foreground', icon: Loader2, animate: true },
  chunking: { label: 'Chunking', color: 'bg-secondary text-muted-foreground dark:bg-card dark:text-muted-foreground', icon: Loader2, animate: true },
  embedding: { label: 'Embedding', color: 'bg-secondary text-muted-foreground dark:bg-card dark:text-muted-foreground', icon: Loader2, animate: true },
  indexed: { label: 'Indexed', color: 'bg-secondary text-muted-foreground dark:bg-card dark:text-muted-foreground', icon: Database },
  failed: { label: 'Failed', color: 'bg-destructive/10 text-destructive  ', icon: AlertCircle },
};

const INGESTION_IN_FLIGHT_STATUSES = ['queued', 'parsing', 'analyzing', 'chunking', 'embedding'];
// Offer repair only for formats with a supported extraction pipeline.
// The API may return extensions with or without a leading dot.
const isPipelineEligible = (doc) =>
  ['pdf', '.pdf', 'xlsx', '.xlsx', 'docx', '.docx', 'pptx', '.pptx', 'png', '.png'].includes((doc.file_type || '').toLowerCase());
// Retry Ingestion is an index repair (EIP-514): offered when ingestion failed
// outright OR the backend reports no trustworthy page index on a doc that is
// not mid-ingestion (the server refuses to race an in-flight run anyway).
// page_index_ready comes from the documents API; strict === false keeps older
// cached rows (undefined) from offering a retry they may not need.
const canRetryIngestion = (doc) =>
  isPipelineEligible(doc) &&
  (doc.ingestion_status === 'failed' ||
    (doc.page_index_ready === false && !INGESTION_IN_FLIGHT_STATUSES.includes(doc.ingestion_status)));
// A published/live doc still answers in chat without a page index (whole-doc
// reads), but gets none of the page-span behavior; the list badge surfaces it.
const showsNoIndexBadge = (doc) =>
  isPipelineEligible(doc) &&
  doc.page_index_ready === false &&
  ['published', 'live'].includes(doc.status) &&
  !INGESTION_IN_FLIGHT_STATUSES.includes(doc.ingestion_status);

const UPLOAD_PIPELINE_STAGES = [
  { key: 'upload', label: 'Upload' },
  { key: 'validation', label: 'Validation' },
  { key: 'preparation', label: 'Preparation' },
  { key: 'extraction', label: 'Extraction' },
];

const getFileExtension = (filename = '') => {
  const match = String(filename).toLowerCase().match(/\.([a-z0-9]+)$/);
  return match?.[1] || '';
};

const getDocumentTypeMeta = (filename = '') => {
  const ext = getFileExtension(filename);
  if (ext === 'pdf') return { label: 'PDF', icon: FileText, className: 'text-slate-600 bg-slate-100 border-transparent dark:bg-slate-800/60 dark:text-slate-300' };
  if (ext === 'docx' || ext === 'doc') return { label: 'DOC', icon: FileType, className: 'text-blue-500 bg-blue-500/10 border-blue-500/20' };
  if (ext === 'pptx' || ext === 'ppt') return { label: 'PPT', icon: FileType, className: 'text-orange-500 bg-orange-500/10 border-orange-500/20' };
  if (ext === 'txt') return { label: 'TXT', icon: FileText, className: 'text-slate-500 bg-slate-500/10 border-slate-500/20' };
  return { label: ext ? ext.toUpperCase() : 'FILE', icon: File, className: 'text-muted-foreground bg-muted border-border' };
};

function DocumentTypeBadge({ filename }) {
  const meta = getDocumentTypeMeta(filename);
  const TypeIcon = meta.icon;
  return (
    <span className={cn('inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border', meta.className)} title={meta.label}>
      <TypeIcon className="h-4 w-4" />
    </span>
  );
}

function UploadStatusPill({ status, stage, analyzeWithEva }) {
  const statusMeta = (() => {
    if (status === 'failed') return { label: 'Failed', icon: XCircle, className: 'border-destructive/30 bg-destructive/10 text-destructive' };
    if (status === 'completed' && analyzeWithEva) return { label: 'Uploaded + analyzed', icon: Sparkles, className: 'border-primary/30 bg-primary/10 text-primary' };
    if (status === 'completed') return { label: 'Uploaded', icon: CheckCircle, className: 'border-muted-foreground/20 bg-muted text-muted-foreground' };
    if (status === 'uploading' && stage === 'extraction') return { label: 'Analyzing', icon: Sparkles, className: 'border-primary/30 bg-primary/10 text-primary', pulse: true };
    if (status === 'uploading') return { label: 'Uploading', icon: Upload, className: 'border-blue-500/30 bg-blue-500/10 text-blue-500' };
    return { label: 'Pending', icon: Clock, className: 'border-border bg-muted/60 text-muted-foreground' };
  })();
  const StatusIcon = statusMeta.icon;

  return (
    <Badge className={cn('inline-flex min-w-[9.75rem] justify-center gap-1.5 rounded-full border px-2.5 py-1 font-medium', statusMeta.className)}>
      <StatusIcon className={cn('h-3.5 w-3.5', statusMeta.pulse && 'animate-pulse')} />
      {statusMeta.label}
    </Badge>
  );
}

const parseStringListField = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];

  const trimmed = value.trim();
  if (!trimmed) return [];

  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return trimmed
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  }
};

const normalizeGroupListResponse = (data) => {
  const groups = Array.isArray(data) ? data : (data?.groups || []);
  return groups.filter(group => {
    const slug = String(group.slug || '').toLowerCase();
    const id = String(group.id || '').toLowerCase();
    const name = String(group.name || '').trim().toLowerCase();
    return slug !== 'all-employees' && id !== 'all' && name !== 'all employees';
  });
};

// --- Group resolution for the KB Content table (EIP-292) ---------------------
// group_visibility holds group ids OR legacy slugs; [] or ["all"] means public.
// These helpers resolve to display names for the Groups column, and back the
// client-side Groups sort + filter. Kept module-level so display, sort, and
// filter all agree.
const GROUP_ALL_EMPLOYEES_LABEL = 'All Employees';
export const GROUP_FILTER_ALL_EMPLOYEES = '__all_employees__';

const docGroupIsAllEmployees = (groupIds) =>
  groupIds.length === 0 || groupIds.includes('all');

export const resolveDocGroupNames = (doc, availableGroups) => {
  const groupIds = Array.isArray(doc.group_visibility) ? doc.group_visibility : [];
  if (docGroupIsAllEmployees(groupIds)) return [GROUP_ALL_EMPLOYEES_LABEL];
  return groupIds.map((groupId) => {
    const group = availableGroups.find((g) => g.id === groupId || g.slug === groupId);
    return group?.name || groupId;
  });
};

// Stable, alphabetical join so the Groups column sorts by display name.
export const groupSortKey = (doc, availableGroups) =>
  resolveDocGroupNames(doc, availableGroups)
    .slice()
    .sort((a, b) => a.localeCompare(b))
    .join(', ');

// Literal column-value filter: '' = all, GROUP_FILTER_ALL_EMPLOYEES = public
// only, otherwise a group id (matched against the doc's ids OR the group's slug).
export const docMatchesGroupFilter = (doc, groupFilter, availableGroups) => {
  if (!groupFilter) return true;
  const groupIds = Array.isArray(doc.group_visibility) ? doc.group_visibility : [];
  const isAllEmployees = docGroupIsAllEmployees(groupIds);
  if (groupFilter === GROUP_FILTER_ALL_EMPLOYEES) return isAllEmployees;
  if (isAllEmployees) return false;
  const group = availableGroups.find((g) => g.id === groupFilter);
  return groupIds.includes(groupFilter) || (group ? groupIds.includes(group.slug) : false);
};

// --- Topic resolution for the Content table (EIP-312) -----------------------
// dim_kb_documents.topic is a nullable, denormalized topic *name* with three
// stored "no topic" representations: 'Uncategorized' (bulk upload), NULL
// (ingestion), and '' (metadata edit). Display and filter both collapse them to
// one label, so a row the filter returns always visibly says why.
export const UNCATEGORIZED_TOPIC_LABEL = 'Uncategorized';

// Out-of-band token — topic names are user-supplied, so the bare label can't
// serve as the filter value. Same convention as GROUP_FILTER_ALL_EMPLOYEES.
// Must stay in sync with TOPIC_FILTER_UNCATEGORIZED in backend/routers/kb.py.
export const TOPIC_FILTER_UNCATEGORIZED = '__uncategorized__';

export const docTopicLabel = (doc) =>
  (doc?.topic || '').trim() || UNCATEGORIZED_TOPIC_LABEL;

export const docIsUncategorized = (doc) =>
  docTopicLabel(doc) === UNCATEGORIZED_TOPIC_LABEL;

// Mirrors the SQL predicate in list_kb_documents. Applied client-side as well
// as server-side because the Smart Views fetch from /kb/documents/performance,
// which takes only `view` — without this the filter is inert there.
export const docMatchesTopicFilter = (doc, topicFilter) => {
  if (!topicFilter) return true;
  if (topicFilter === TOPIC_FILTER_UNCATEGORIZED) return docIsUncategorized(doc);
  return doc.topic === topicFilter;
};

const findAllEmployeesGroup = (data) => {
  const groups = Array.isArray(data) ? data : (data?.groups || []);
  return groups.find(group => {
    const slug = String(group.slug || '').toLowerCase();
    const id = String(group.id || '').toLowerCase();
    const name = String(group.name || '').trim().toLowerCase();
    return slug === 'all-employees' || id === 'all' || name === 'all employees';
  }) || null;
};

const getAllEmployeesCount = (data) => {
  const group = findAllEmployeesGroup(data);
  return getGroupMemberCount(group);
};

const ALL_EMPLOYEES_GROUP_COLOR = '#64748b';
const DEFAULT_GROUP_COLOR = '#3b82f6';

const getGroupColor = (group) => group?.color || DEFAULT_GROUP_COLOR;

const getGroupMemberCount = (group) => {
  const count = Number(group?.member_count);
  return Number.isFinite(count) ? count : null;
};

const formatEmployeeCount = (count) => {
  if (count == null) return null;
  return `${count} employee${count === 1 ? '' : 's'}`;
};

function GroupShieldIcon({ group, all = false, className }) {
  const color = all ? ALL_EMPLOYEES_GROUP_COLOR : getGroupColor(group);

  return (
    <span
      className={cn('flex shrink-0 items-center justify-center rounded-xl border bg-secondary', className)}
      style={{
        color,
        borderColor: all ? `${ALL_EMPLOYEES_GROUP_COLOR}55` : `${color}55`,
      }}
    >
      <Shield className="h-4 w-4" />
    </span>
  );
}

const formatBulkUploadError = (error) => {
  const parsed = parseErrorDetail(error);
  const rawMessage = parsed.message || 'Upload failed';
  const message = String(rawMessage).trim();
  const code = parsed.error || parsed.code || error?.response?.data?.detail?.error;

  if (code === 'kb_token_limit_reached' || /AI usage capacity|knowledge base (tokens?|usage).*(limit|capacity|reached)|kb token/i.test(message)) {
    return {
      message,
      type: 'token_limit',
      label: 'KB usage limit',
    };
  }

  if (code === 'document_limit_reached' || /document.*(capacity|limit).*reached|maximum .* documents/i.test(message)) {
    return {
      message,
      type: 'document_limit',
      label: 'Document limit',
    };
  }

  if (/pages?.*(exceed|limit)|plan limit|pages per document/i.test(message)) {
    return {
      message,
      type: 'plan_limit',
      label: 'Page limit',
    };
  }

  if (/field required/i.test(message)) {
    return {
      message: 'The upload request was missing required metadata. Please try again, and if it repeats, upload this file individually so the missing field can be corrected.',
      type: 'validation',
      label: 'Needs review',
    };
  }

  return {
    message: message || 'Upload failed. Please try again.',
    type: 'error',
    label: 'Upload failed',
  };
};

function DocumentEmptyState({ activeView, canUpload, onUpload }) {
  const copy = {
    top: {
      icon: TrendingUp,
      title: 'No top performers yet',
      description: 'Documents with high answer usage will appear here once employees start asking Eva questions.',
    },
    'needs-attention': {
      icon: AlertCircle,
      title: 'Nothing needs attention',
      description: 'Documents with low helpfulness ratings will appear here when there is enough feedback.',
    },
    unused: {
      icon: Clock,
      title: 'No unused documents',
      description: 'Documents with zero recent usage will appear here as your knowledge base grows.',
    },
    gaps: {
      icon: Search,
      title: 'No content gaps detected',
      description: 'Questions without matching documents will appear here once employees interact with Eva.',
    },
    all: {
      icon: FileText,
      title: 'No documents found',
      description: canUpload
        ? 'Upload your first document to start building Eva’s knowledge base.'
        : 'Documents will appear here once your team adds content.',
    },
  };
  const state = copy[activeView] || copy.all;
  return (
    <EmptyState
      icon={state.icon}
      title={state.title}
      description={state.description}
      action={activeView === 'all' && canUpload}
      actionLabel="Upload document"
      actionIcon={Upload}
      onAction={activeView === 'all' && canUpload ? onUpload : null}
      variant="plain"
    />
  );
}

function PdfFileIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M7 3h6l4 4v14H7V3Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M13 3v5h4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path
        d="M8.5 16.5h7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M8.5 12.5h7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PlanRulesCard({ maxPagesPerDocument }) {
  const pageLimit = maxPagesPerDocument === null || maxPagesPerDocument === undefined || maxPagesPerDocument === -1
    ? 'Custom'
    : new Intl.NumberFormat('en-US').format(Number(maxPagesPerDocument || 0));

  return (
    <Card className="h-full">
      <CardContent className="p-4 sm:p-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground">Upload Plan Rules</p>
              <p className="text-sm text-muted-foreground">Action constraints</p>
            </div>
          </div>
          <Badge variant="outline" className="border-primary/30 bg-primary/10 text-primary">
            <ShieldCheck className="mr-1 h-3 w-3" />
            Plan rule
          </Badge>
        </div>

        <div className="rounded-lg border bg-muted/20 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pages per document</p>
          <p className="mt-2 text-3xl font-bold text-foreground">
            {pageLimit === 'Custom' ? 'Custom' : `Max ${pageLimit}`}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">Applied during single and bulk upload.</p>
        </div>

        <div className="mt-4 border-t border-border pt-3 text-sm text-muted-foreground">
          Documents over this page count are blocked before they enter the knowledge base.
        </div>
      </CardContent>
    </Card>
  );
}

// File type icons and colors
const FILE_TYPE_CONFIG = {
  pdf: { icon: PdfFileIcon, color: 'text-slate-600 dark:text-slate-300', bg: 'bg-slate-100 dark:bg-slate-800/60' },
  docx: { icon: FileType, color: 'text-info', bg: 'bg-info/10' },
  doc: { icon: FileType, color: 'text-info', bg: 'bg-info/10' },
  txt: { icon: File, color: 'text-muted-foreground', bg: 'bg-secondary' },
  xlsx: { icon: FileType, color: 'text-success', bg: 'bg-success/10' },
  xls: { icon: FileType, color: 'text-success', bg: 'bg-success/10' },
  pptx: { icon: FileType, color: 'text-warning', bg: 'bg-warning/10' },
  ppt: { icon: FileType, color: 'text-warning', bg: 'bg-warning/10' },
  md: { icon: FileText, color: 'text-primary', bg: 'bg-primary/10' },
  html: { icon: FileText, color: 'text-cyan-600', bg: 'bg-cyan-50' },
};

function getFileTypeConfig(fileType) {
  const type = (fileType || 'pdf').toLowerCase().replace('.', '');
  return FILE_TYPE_CONFIG[type] || FILE_TYPE_CONFIG.pdf;
}

function IngestionStatusBadge({ doc }) {
  if (doc.status !== 'published' || !doc.ingestion_status) {
    return <span className="text-muted-foreground">—</span>;
  }

  const config = INGESTION_CONFIG[doc.ingestion_status] || INGESTION_CONFIG.queued;
  const Icon = config.icon;

  return (
    <div className="flex items-center gap-1">
      <Badge className={`${config.color} border-0`} data-testid={`ingestion-${doc.ingestion_status}`}>
        <Icon className={`w-3 h-3 mr-1 ${config.animate ? 'animate-spin' : ''}`} />
        {config.label}
      </Badge>
      {doc.ingestion_status === 'indexed' && doc.chunk_count && (
        <span className="text-xs text-muted-foreground ml-1">{doc.chunk_count} pages</span>
      )}
    </div>
  );
}

function PerformanceCell({ doc, showPerformance }) {
  // Performance views (top/needs-attention/unused) return docs with status
  // 'published' OR 'live'; both must emit the Retrievals/Helpful cells so the row
  // stays aligned with the header columns (EIP-198).
  if (!showPerformance || (doc.status !== 'published' && doc.status !== 'live')) {
    return null;
  }

  const retrievals = doc.retrievals || 0;
  const helpfulRate = doc.helpful_rate || 0;

  // Determine performance indicator
  let performanceIcon = null;
  let performanceColor = 'text-muted-foreground';
  
  if (retrievals > 10 && helpfulRate >= 0.8) {
    performanceIcon = <Flame className="w-4 h-4 text-success" />;
    performanceColor = 'text-success';
  } else if (retrievals > 5 && helpfulRate < 0.7) {
    performanceIcon = <AlertCircle className="w-4 h-4 text-warning" />;
    performanceColor = 'text-warning';
  } else if (retrievals === 0) {
    performanceIcon = <Clock className="w-4 h-4 text-muted-foreground" />;
    performanceColor = 'text-muted-foreground';
  }

  return (
    <>
      <td className="p-3 sm:p-4 hidden xl:table-cell">
        <div className="flex items-center gap-2">
          {performanceIcon}
          <span className={`font-medium ${performanceColor}`}>{retrievals}</span>
        </div>
      </td>
      <td className="p-3 sm:p-4 hidden xl:table-cell">
        {retrievals > 0 ? (
          <div className="flex items-center gap-1">
            <ThumbsUp className={`w-3 h-3 ${helpfulRate >= 0.8 ? 'text-success' : helpfulRate >= 0.6 ? 'text-warning' : 'text-destructive'}`} />
            <span className={`text-sm font-medium ${helpfulRate >= 0.8 ? 'text-success' : helpfulRate >= 0.6 ? 'text-warning' : 'text-destructive'}`}>
              {(helpfulRate * 100).toFixed(0)}%
            </span>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
    </>
  );
}

function GroupsCell({ doc, availableGroups }) {
  const groupIds = Array.isArray(doc.group_visibility) ? doc.group_visibility : [];
  const isAllEmployees = docGroupIsAllEmployees(groupIds);

  if (isAllEmployees) {
    return (
      <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-border bg-secondary px-2 py-0.5 text-xs font-medium text-foreground">
        <GroupShieldIcon all className="h-4 w-4 rounded-md" />
        All Employees
      </span>
    );
  }

  const names = resolveDocGroupNames(doc, availableGroups);

  return (
    <div className="flex flex-wrap gap-1">
      {names.slice(0, 2).map((name, idx) => (
        <Badge key={idx} variant="outline" className="w-fit px-1.5 py-0 text-xs text-foreground">
          {name}
        </Badge>
      ))}
      {names.length > 2 && (
        <Badge
          variant="outline"
          className="px-1.5 py-0 text-xs text-muted-foreground"
          title={names.slice(2).join(', ')}
        >
          +{names.length - 2}
        </Badge>
      )}
    </div>
  );
}

function DocumentRow({ doc, onAction, canUpload, canApprove, canPublish, canDelete, showPerformance, isSelected, onSelect, topicsMap, isFlashing, availableGroups, compact = false }) {
  const actionTriggerRef = useRef(null);
  const baseStatus = STATUS_CONFIG[doc.status] || STATUS_CONFIG.draft;
  const tags = parseStringListField(doc.tags);
  const fileConfig = getFileTypeConfig(doc.file_type);
  const FileIcon = fileConfig.icon;
  
  // Get topic info from the topics map (referential integrity)
  const topicInfo = topicsMap?.[doc.topic] || null;
  
  // Determine effective status for display (Testing vs Live for published docs)
  const getEffectiveStatus = () => {
    // Handle explicit 'live' status first
    if (doc.status === 'live') {
      return { label: 'Live', color: 'bg-success/10 text-success', icon: Globe };
    }
    if (doc.status === 'published') {
      if (doc.ingestion_status === 'indexed') {
        return { label: 'Live', color: 'bg-success/10 text-success', icon: Globe };
      }
      if (['queued', 'parsing', 'analyzing', 'chunking', 'embedding'].includes(doc.ingestion_status)) {
        return { label: 'Ingesting', color: 'bg-warning/10 text-warning', icon: Loader2 };
      }
      if (doc.ingestion_status === 'failed') {
        return { label: 'Failed', color: 'bg-destructive/10 text-destructive', icon: AlertCircle };
      }
    }
    return baseStatus;
  };

  const status = getEffectiveStatus();
  const StatusIcon = status.icon;

  // Context-sensitive actions based on document status and workflow state
  const getContextActions = () => {
    const actions = [];
    
    // View Details is always available
    actions.push({ key: 'details', label: 'View Details', icon: Info });
    actions.push({ key: 'download', label: 'Download', icon: Download });
    
    // Status-specific actions
    switch (doc.status) {
      case 'draft':
        if (canUpload) {
          actions.push({ key: 'move-folder', label: 'Move to Folder', icon: Folder });
          actions.push({ key: 'submit', label: 'Submit for Review', icon: Send });
        }
        break;
      case 'pending_review':
        if (canApprove) {
          actions.push({ key: 'approve', label: 'Approve', icon: CheckCircle, className: 'text-success' });
          actions.push({ key: 'reject', label: 'Reject', icon: XCircle, className: 'text-warning' });
        }
        if (canUpload) {
          actions.push({ key: 'move-folder', label: 'Move to Folder', icon: Folder });
        }
        break;
      case 'approved':
        if (canPublish) {
          actions.push({ key: 'publish', label: 'Start Ingestion', icon: CheckCheck, className: 'text-success' });
        }
        if (canUpload) {
          actions.push({ key: 'move-folder', label: 'Move to Folder', icon: Folder });
        }
        break;
      case 'published':
        // Workflow actions based on ingestion status
        if (canRetryIngestion(doc) && canPublish) {
          actions.push({ key: 'retry-ingestion', label: 'Retry Ingestion', icon: RefreshCw });
        }
        if (canUpload) {
          actions.push({ key: 'move-folder', label: 'Move to Folder', icon: Folder });
        }
        actions.push({ key: 'archive', label: 'Archive', icon: Archive });
        break;
      case 'live':
        if (canRetryIngestion(doc) && canPublish) {
          actions.push({ key: 'retry-ingestion', label: 'Retry Ingestion', icon: RefreshCw });
        }
        if (canUpload) {
          actions.push({ key: 'move-folder', label: 'Move to Folder', icon: Folder });
        }
        actions.push({ key: 'archive', label: 'Archive', icon: Archive });
        break;
      case 'archived':
        if (canUpload) {
          actions.push({ key: 'restore', label: 'Restore to Draft', icon: RefreshCw });
          actions.push({ key: 'move-folder', label: 'Move to Folder', icon: Folder });
        }
        break;
      default:
        break;
    }
    
    // Delete is available for appropriate roles
    if (canDelete && ((doc.status !== 'published' && doc.status !== 'live') || doc.ingestion_status === 'failed')) {
      actions.push({ key: 'delete', label: 'Delete', icon: Trash2, className: 'text-destructive' });
    }
    
    return actions;
  };

  const contextActions = getContextActions();
  const documentMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button ref={actionTriggerRef} variant="ghost" size="sm" className={compact ? 'h-11 w-11 shrink-0 p-0' : undefined} aria-label={`Actions for ${doc.title}`} data-testid={`doc-menu-${doc.id}`}>
          <MoreVertical className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {contextActions.map((action) => (
          <DropdownMenuItem key={action.key} onClick={() => onAction(action.key, doc, { returnFocus: actionTriggerRef.current })} className={cn(action.className, compact && 'min-h-11')}>
            <action.icon className="w-4 h-4 mr-2" /> {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
  const statusBadges = (
    <div className="flex flex-wrap items-start gap-1.5">
      <Badge className={`${status.color} border-0`}>
        <StatusIcon className={`w-3 h-3 mr-1 ${status.label === 'Ingesting' ? 'animate-spin' : ''}`} />
        {status.label}
      </Badge>
      {doc.readable_by_eva === false && (
        <Badge className="border-warning/30 bg-warning/10 text-warning text-xs font-medium" title="Eva cannot read this document yet. Retry ingestion for a supported format, or upload a reviewed PDF." data-testid={`doc-unreadable-${doc.id}`}>
          <AlertCircle className="w-3 h-3 mr-1" />Not readable by Eva
        </Badge>
      )}
      {showsNoIndexBadge(doc) && (
        <Badge className="border-warning/30 bg-warning/10 text-warning text-xs font-medium" title="No page index exists for this document, so chat falls back to reading the whole file. Use Retry Ingestion to rebuild the index." data-testid={`doc-noindex-${doc.id}`}>
          <AlertCircle className="w-3 h-3 mr-1" />Not searchable
        </Badge>
      )}
    </div>
  );

  if (compact) {
    return (
      <li className={cn('min-w-0 space-y-3 border-b border-border p-4 last:border-b-0', isSelected && 'bg-primary/5', isFlashing && 'animate-flash-row bg-success/10')} data-testid={`doc-row-${doc.id}`}>
        <div className="flex items-center gap-2">
          <label className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center" htmlFor={`select-document-${doc.id}`}>
            <Checkbox id={`select-document-${doc.id}`} checked={isSelected} onCheckedChange={() => onSelect(doc.id)} aria-label={`Select ${doc.title}`} data-testid={`select-doc-${doc.id}`} />
          </label>
          <FileIcon aria-hidden="true" className={cn('h-5 w-5 shrink-0', fileConfig.color)} />
          <span className="min-w-0 flex-1 text-sm text-muted-foreground">
            {(doc.file_type || 'PDF').replace('.', '').toUpperCase()} · {doc.pages} {['docx', 'xlsx'].includes((doc.file_type || '').replace('.', '').toLowerCase()) ? 'section' : 'page'}{doc.pages !== 1 && 's'}
          </span>
          {documentMenu}
        </div>
        <button type="button" onClick={(event) => onAction('details', doc, { returnFocus: event.currentTarget })} className="block min-h-11 w-full rounded text-left text-base font-semibold leading-snug text-foreground [overflow-wrap:anywhere] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          {doc.title}
        </button>
        {doc.original_filename && <p className="text-xs text-muted-foreground [overflow-wrap:anywhere]">{doc.original_filename}</p>}
        {statusBadges}
        <dl className="space-y-2 text-sm">
          <div><dt className="text-xs text-muted-foreground">Topic</dt><dd className="[overflow-wrap:anywhere]">{docTopicLabel(doc)}{doc.sub_topic && ` / ${doc.sub_topic}`}</dd></div>
          <div className="flex flex-wrap items-center gap-2"><dt className="sr-only">Access</dt><dd className="max-w-full [overflow-wrap:anywhere]"><GroupsCell doc={doc} availableGroups={availableGroups} /></dd>{(doc.country || doc.region) && <><dt className="sr-only">Location</dt><dd className="text-muted-foreground [overflow-wrap:anywhere]">{[doc.region, doc.country].filter(Boolean).join(' / ')}</dd></>}</div>
          {showPerformance && ['published', 'live'].includes(doc.status) && <div className="flex flex-wrap gap-x-4 gap-y-1"><dt>Retrievals</dt><dd>{doc.retrievals || 0}</dd><dt>Helpful</dt><dd>{doc.retrievals > 0 ? `${Math.round((doc.helpful_rate || 0) * 100)}%` : 'No ratings'}</dd></div>}
        </dl>
      </li>
    );
  }

  // Handle row click (open details)
  const handleRowClick = (e) => {
    // Don't trigger if clicking on checkbox, buttons, or dropdown
    if (e.target.closest('button') || e.target.closest('[role="checkbox"]') || e.target.closest('[role="menuitem"]')) {
      return;
    }
    onAction('details', doc, { returnFocus: actionTriggerRef.current });
  };

  return (
    <tr 
      className={`border-b border-border hover:bg-secondary/50 transition-colors cursor-pointer ${isSelected ? 'bg-primary/5' : ''} ${isFlashing ? 'animate-flash-row bg-success/10' : ''}`}
      onClick={handleRowClick}
      data-testid={`doc-row-${doc.id}`}
    >
      <td className="p-3 sm:p-4">
        <div className="flex min-w-0 items-center gap-3">
          <Checkbox 
            checked={isSelected} 
            onCheckedChange={() => onSelect(doc.id)} 
            data-testid={`select-doc-${doc.id}`}
            aria-label={`Select ${doc.title}`}
            className="flex-shrink-0"
          />
          <div className={`w-10 h-10 ${fileConfig.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
            <FileIcon className={`w-5 h-5 ${fileConfig.color}`} />
          </div>
          <div className="min-w-0 max-w-full flex-1">
            <p className="truncate font-medium text-foreground" title={doc.title}>
              {doc.title}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              {doc.original_filename && (
                <span className="text-xs text-muted-foreground truncate max-w-[180px]" title={doc.original_filename}>
                  {doc.original_filename}
                </span>
              )}
              <span className="text-sm text-muted-foreground">{(doc.file_type || 'PDF').toUpperCase()} · {doc.pages} {['docx', 'xlsx'].includes((doc.file_type || '').replace('.', '').toLowerCase()) ? 'sections' : 'pages'}</span>
              {tags.length > 0 && (
                <div className="flex gap-1 flex-wrap">
                  {tags.slice(0, 2).map((tag, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs px-1.5 py-0 border-primary/30 text-primary">
                      <Tag className="w-2.5 h-2.5 mr-0.5" />{tag}
                    </Badge>
                  ))}
                  {tags.length > 2 && (
                    <Badge variant="outline" className="text-xs px-1.5 py-0 text-muted-foreground">+{tags.length - 2}</Badge>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </td>
      <td className="p-3 sm:p-4 hidden md:table-cell">
        <div className="flex flex-col gap-1">
          {doc.topic && topicInfo ? (
            <Badge className="border border-border bg-secondary text-foreground text-xs font-medium flex items-center gap-1.5 w-fit">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: topicInfo.color || 'hsl(var(--muted-foreground))' }}
              />
              {doc.topic}
            </Badge>
          ) : (
            // No `null` branch: an untopiced doc used to render a blank cell,
            // yet the Uncategorized filter returns it — the two contradicted
            // each other. Muted to match the details dialog. (EIP-312)
            <Badge className={cn(
              'bg-secondary text-foreground border-border border text-xs font-medium flex items-center gap-1 w-fit',
              docIsUncategorized(doc) && 'text-muted-foreground'
            )}>
              <Bookmark className="w-3 h-3" />
              {docTopicLabel(doc)}
            </Badge>
          )}
          {doc.sub_topic && (
            <span className="text-xs text-muted-foreground pl-1">
              └ {doc.sub_topic}
            </span>
          )}
        </div>
      </td>
      <td className="p-3 sm:p-4 hidden lg:table-cell">
        <div className="flex flex-col gap-0.5 text-sm">
          {doc.region && (
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <Globe className="w-3.5 h-3.5" />
              {doc.region}
            </span>
          )}
          {doc.country && (
            <span className="flex items-center gap-1.5 text-foreground">
              <MapPin className="w-3.5 h-3.5" />
              {doc.country}
            </span>
          )}
          {!doc.region && !doc.country && (
            <span className="text-muted-foreground">—</span>
          )}
        </div>
      </td>
      <td className="p-3 sm:p-4 hidden lg:table-cell">
        <GroupsCell doc={doc} availableGroups={availableGroups} />
      </td>
      <td className="p-3 sm:p-4">{statusBadges}</td>
      <PerformanceCell doc={doc} showPerformance={showPerformance} />
      <td className="p-3 sm:p-4 hidden lg:table-cell">
        <IngestionStatusBadge doc={doc} />
      </td>
      <td className="p-3 sm:p-4">{documentMenu}</td>
    </tr>
  );
}

function DocumentPreviewDialog({ doc, open, onOpenChange }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (open && doc) {
      fetchPreview(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc]);

  const fetchPreview = async (page) => {
    setLoading(true);
    try {
      const response = await api.get(`/kb/documents/${doc.id}/preview?page=${page}`);
      setPreview(response.data);
      setCurrentPage(page);
    } catch (err) {
      console.error('Failed to fetch preview:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-info" />
            Document Preview: {doc?.title}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto bg-secondary rounded-xl p-4">
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
            </div>
          ) : preview?.type === 'image' ? (
            <div className="text-center">
              <img 
                src={`data:${preview.mime};base64,${preview.data}`} 
                alt={`Page ${currentPage}`}
                className="max-w-full mx-auto shadow-lg rounded"
              />
              {preview.total_pages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchPreview(currentPage - 1)}
                    disabled={currentPage <= 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {['worksheet_rows', 'word_sections'].includes(preview.location_type) ? 'Section' : 'Page'} {currentPage} of {preview.total_pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchPreview(currentPage + 1)}
                    disabled={currentPage >= preview.total_pages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          ) : preview?.type === 'text' ? (
            <div className="bg-card rounded-xl p-6 font-mono text-sm whitespace-pre-wrap max-h-[60vh] overflow-auto">
              {preview.data}
              {preview.truncated && (
                <p className="text-warning mt-4 font-sans">
                  [Content truncated for preview...]
                </p>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              Preview not available for this document type.
            </p>
          )}
        </div>

        {/* Document Info */}
        <div className="border-t border-border pt-4 mt-4">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Topic:</span>
              <div className="ml-2 text-foreground">
                {doc?.topic}
                {doc?.sub_topic && (
                  <span className="text-muted-foreground ml-1">/ {doc?.sub_topic}</span>
                )}
              </div>
            </div>
            <div>
              <span className="text-muted-foreground">Location:</span>
              <div className="ml-2 text-foreground flex items-center gap-2 flex-wrap">
                {doc?.region && (
                  <span className="flex items-center gap-1">
                    <Globe className="w-3 h-3" />
                    {doc?.region}
                  </span>
                )}
                {doc?.country && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {doc?.country}
                  </span>
                )}
              </div>
            </div>
            <div>
              <span className="text-muted-foreground">Status:</span>
              <Badge className={`ml-2 ${STATUS_CONFIG[doc?.status]?.color || ''}`}>
                {STATUS_CONFIG[doc?.status]?.label || doc?.status}
              </Badge>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DocumentDetailsDialog({ doc, details, loading, error, onRetry, open, onOpenChange, onCloseAutoFocus, topicsMap, onAction, canPublish, canApprove, canUpload, canDelete }) {
  const { hasRole } = useAuth();
  const [editing, setEditing] = useState(false);
  const [suggestOnOpen, setSuggestOnOpen] = useState(false);
  const editorRef = useRef(null);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [allEmployeesCount, setAllEmployeesCount] = useState(null);
  const [workflowExpanded, setWorkflowExpanded] = useState(false);
  const [perfStats, setPerfStats] = useState(null);
  const [perfError, setPerfError] = useState(false);
  const data = details || doc || {};
  const fileConfig = getFileTypeConfig(data.file_type);
  const FileIcon = fileConfig.icon;
  const tags = parseStringListField(data.tags);
  const topicName = data.topic || 'Uncategorized';
  const isUncategorized = topicName === 'Uncategorized';
  const subTopicName = data.sub_topic || 'No sub-topic';
  const hasSubTopic = !!data.sub_topic;
  const topicInfo = topicsMap?.[topicName];
  const isDraft = data.status === 'draft';
  const isPendingReview = data.status === 'pending_review';
  const isApproved = data.status === 'approved';
  const isPublished = ['published', 'live'].includes(data.status);
  const isArchived = data.status === 'archived';
  const isLive = data.status === 'live' || (data.status === 'published' && ['indexed', 'complete'].includes(data.ingestion_status));
  const assignedMetadataEditable = isDraft || isPublished;
  const isAdmin = hasRole(['admin']);
  useEffect(() => { setEditing(false); setSuggestOnOpen(false); setWorkflowExpanded(false); }, [open, doc?.id]);
  useEffect(() => {
    if (!open || !doc?.id) return;
    let active = true;
    setPerfStats(null); setPerfError(false);
    if (['published', 'live'].includes(doc.status)) api.get(`/kb/documents/${doc.id}/performance`)
      .then(res => { if (active) setPerfStats(res.data?.performance || null); })
      .catch(() => { if (active) setPerfError(true); });
    api.get('/groups').then(res => { if (active) {
      setAvailableGroups(normalizeGroupListResponse(res.data)); setAllEmployeesCount(getAllEmployeesCount(res.data));
    } }).catch(() => { if (active) { setAvailableGroups([]); setAllEmployeesCount(null); } });
    return () => { active = false; };
  }, [open, doc?.id, doc?.status]);
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { 
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
      });
    } catch {
      return dateStr;
    }
  };
  
  const startEditing = (suggest = false) => { setSuggestOnOpen(suggest); setEditing(true); };
  const requestClose = async () => {
    if (editing && !await editorRef.current?.mayDiscard()) return;
    setEditing(false); onOpenChange?.(false);
  };
  // Handle workflow actions
  const handleWorkflowAction = async (action) => {
    await onAction?.(action, doc, { keepDetailsOpen: true });
  };
  
  const renderField = (label, value) => <div>
    <Label className="text-xs text-muted-foreground uppercase tracking-wide">{label}</Label>
    <p className="mt-1 min-h-[84px] max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-secondary p-3 text-sm text-foreground dark:bg-card">{value || 'Not provided'}</p>
  </div>;
  // Early return after hooks
  if (!doc) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) requestClose(); }}>
        <DialogContent onCloseAutoFocus={onCloseAutoFocus} className="max-w-3xl max-h-[90dvh] overflow-hidden flex flex-col" closeClassName="h-11 w-11 flex items-center justify-center right-2 top-2" closeLabel="Close document">
        <DialogHeader className="pr-8 text-left">
          <DialogTitle className="flex min-w-0 items-center gap-3">
            <div className={`w-10 h-10 shrink-0 ${fileConfig.bg} rounded-xl flex items-center justify-center`}>
              <FileIcon className={`w-5 h-5 ${fileConfig.color}`} />
            </div>
            <div className="min-w-0">
              <span className="block break-words">{data.title}</span>
              <span className="text-sm font-normal text-muted-foreground">
                {(doc.file_type || 'PDF').toUpperCase()} · {doc.pages} {['docx', 'xlsx'].includes((doc.file_type || '').replace('.', '').toLowerCase()) ? 'sections' : 'pages'}
              </span>
            </div>
          </DialogTitle>
          <DialogDescription className="sr-only">
            View document details, metadata, workflow state, and available document actions.
          </DialogDescription>
        </DialogHeader>

        <div className={editing ? "min-h-0 flex-1 flex flex-col overflow-hidden" : "min-h-0 flex-1 overflow-auto space-y-6 py-4"}>
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : error ? (
            <div role="alert" className="space-y-3 py-4"><p className="text-destructive-text">{error}</p><Button className="min-h-11" variant="outline" onClick={onRetry}>Retry document</Button></div>
          ) : editing ? (
            <DocumentMetadataEditor ref={editorRef} key={doc.id} data={data} isAdmin={isAdmin} suggestOnOpen={suggestOnOpen}
              onCancel={() => setEditing(false)} onSaved={metadata => {
                onAction?.('metadata-saved', data, { metadata }); setEditing(false);
                toast.success('Saved', { description: 'Document metadata updated.' });
              }} />
          ) : (
            <>
              {/* Classification Row */}
              <div className="flex flex-wrap gap-3 items-center justify-between">
                <div className="flex flex-wrap gap-3">
                    <Badge className={cn(
                      'min-h-9 whitespace-normal break-words min-w-0 border border-border bg-secondary px-3 py-1 text-sm font-semibold text-foreground shadow-sm',
                      isUncategorized && 'text-muted-foreground'
                    )}>
                      {topicInfo ? (
                        <span
                          className="mr-1.5 h-2 w-2 rounded-full"
                          style={{ backgroundColor: topicInfo.color || 'hsl(var(--muted-foreground))' }}
                        />
                      ) : (
                        <Tag className="mr-1.5 h-3 w-3" />
                      )}
                      {topicName}
                    </Badge>

                    <Badge
                      variant="outline"
                      className={cn(
                        'min-h-9 whitespace-normal break-words min-w-0 border-border bg-secondary px-3 py-1 text-sm font-semibold shadow-sm',
                        !hasSubTopic && 'text-muted-foreground'
                      )}
                    >
                      <Bookmark className="mr-1.5 h-3 w-3" />
                      {subTopicName}
                    </Badge>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => onAction?.('download', doc)}
                    aria-label="Download document"
                    title="Download document"
                    className="h-11 w-11"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  {assignedMetadataEditable && canUpload && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        if (!editing) startEditing();
                      }}
                      aria-label={'Edit document metadata'}
                      title={'Edit document metadata'}
                      aria-pressed={editing}
                      className={cn('h-11 w-11', editing && 'border-primary text-primary')}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  )}
                  {assignedMetadataEditable && canUpload && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => startEditing(true)}
                      aria-label="Ask Eva"
                      title="Ask Eva"
                      className="h-11 w-11 border-primary/40 text-primary hover:bg-primary/10"
                    >
                      <Sparkles className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Workflow Stepper (collapsible for approved+ states) - Actions in footer */}
              {isPublished && data.ingestion_status === 'indexed' ? (
                <Collapsible open={workflowExpanded} onOpenChange={setWorkflowExpanded}>
                  <CollapsibleTrigger className="flex min-h-11 items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full">
                    {workflowExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    Workflow Progress
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="border border-border dark:border-border rounded-lg p-4 bg-muted/50 dark:bg-card/50 mt-2">
                      <DocumentWorkflowStepper 
                        document={data}
                        onAction={handleWorkflowAction}
                        showActions={false}
                      />
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              ) : (
                <div className="border border-border dark:border-border rounded-lg p-4 bg-muted/50 dark:bg-card/50">
                  <DocumentWorkflowStepper
                    document={data}
                    onAction={handleWorkflowAction}
                    showActions={false}
                  />
                </div>
              )}

              {/* Descriptions - Editable in draft */}
              <div className="space-y-4">
                {assignedMetadataEditable || data.short_summary ? (
                  renderField('Short Description', data.short_summary, 'short_summary', true)
                ) : null}
                {assignedMetadataEditable || data.long_summary ? (
                  renderField('Long Description', data.long_summary, 'long_summary', true)
                ) : null}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="flex min-h-14 items-start gap-2">
                  <Globe className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <Label className="text-xs text-muted-foreground">Region</Label>

                      <p className="mt-1 flex h-9 items-center text-sm font-medium">{data.region || '—'}</p>
                  </div>
                </div>
                <div className="flex min-h-14 items-start gap-2">
                  <Building className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <Label className="text-xs text-muted-foreground">Country</Label>

                      <p className="mt-1 flex h-9 items-center text-sm font-medium">
                        {data.region === 'Global' ? '—' : (data.country || '—')}
                      </p>
                  </div>
                </div>
                <div className="flex min-h-14 items-start gap-2">
                  <Hash className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div>
                    <Label className="text-xs text-muted-foreground">Version</Label>
                    <p className="mt-1 flex h-9 items-center text-sm font-medium">v{data.version || 1}</p>
                  </div>
                </div>
                <div className="flex min-h-14 items-start gap-2">
                  <Database className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div>
                    <Label className="text-xs text-muted-foreground">Pages</Label>
                    <p className="mt-1 flex h-9 items-center text-sm font-medium">{data.chunk_count || '—'}</p>
                  </div>
                </div>
                <div className="flex min-h-14 items-start gap-2">
                  <FileText className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div>
                    <Label className="text-xs text-muted-foreground">File Type</Label>
                    <p className="mt-1 flex h-9 items-center text-sm font-medium">{(data.file_type || 'PDF').toUpperCase()}</p>
                  </div>
                </div>
              </div>

              {/* Tags */}
              {tags.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wide">Tags</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {tags.map((tag, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs px-2 py-1 border-primary/30 text-primary">
                        <Tag className="w-3 h-3 mr-1" />{tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Group Visibility */}
              <div>
                <Label className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                  <Users className="w-3 h-3" /> Group Visibility
                </Label>

                  <div className="mt-2">
                    {!data.group_visibility || (Array.isArray(data.group_visibility) && data.group_visibility.includes('all')) ? (
                      <span className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-2.5 py-1 text-xs font-medium text-foreground">
                        <GroupShieldIcon all className="h-5 w-5 rounded-lg" />
                        <span>All Employees</span>
                        {allEmployeesCount != null && (
                          <span className="text-muted-foreground">· {formatEmployeeCount(allEmployeesCount)}</span>
                        )}
                      </span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {(Array.isArray(data.group_visibility) ? data.group_visibility : []).map((groupId, idx) => {
                          const group = availableGroups.find(g => g.id === groupId);
                          return (
                            <span
                              key={idx}
                              className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-2.5 py-1 text-xs font-medium text-foreground"
                            >
                              <GroupShieldIcon group={group} className="h-5 w-5 rounded-lg" />
                              <span>{group?.name || groupId}</span>
                              {group && getGroupMemberCount(group) != null && (
                                <span className="text-muted-foreground">· {formatEmployeeCount(getGroupMemberCount(group))}</span>
                              )}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>

              </div>

              {/* Dates & Approvals */}
              <div className="border-t border-border dark:border-border pt-4 space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-start gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <div>
                      <Label className="text-xs text-muted-foreground">Created</Label>
                      <p className="text-sm">{formatDate(data.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <div>
                      <Label className="text-xs text-muted-foreground">Last Updated</Label>
                      <p className="text-sm">{formatDate(data.updated_at)}</p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-start gap-2">
                    <User className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <div>
                      <Label className="text-xs text-muted-foreground">Uploaded By</Label>
                      <p className="text-sm">{data.uploaded_by_name || data.uploaded_by || '—'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <div>
                      <Label className="text-xs text-muted-foreground">Approved By</Label>
                      <p className="text-sm">{data.approved_by_name || data.approved_by || '—'}</p>
                    </div>
                  </div>
                </div>
                {data.published_at && (
                  <div className="flex items-start gap-2">
                    <CheckCheck className="w-4 h-4 text-success mt-0.5" />
                    <div>
                      <Label className="text-xs text-muted-foreground">Published</Label>
                      <p className="text-sm">{formatDate(data.published_at)}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Retrieval metrics (published/live docs). Prefers the live
                  per-doc fetch (perfStats); falls back to smart-view row fields. */}
              {(data.status === 'published' || data.status === 'live') && (() => {
                const retrievals = perfStats?.retrievals ?? data.retrievals ?? (perfError ? null : 0);
                const helpfulRate = perfStats?.helpful_rate ?? data.helpful_rate;
                const lastRetrieved = perfStats?.last_retrieved_at;
                return (
                  <div className="border-t border-border dark:border-border pt-4">
                    <Label className="text-xs text-muted-foreground uppercase tracking-wide">Retrieval metrics (last 30 days)</Label>
                    <div className="grid grid-cols-3 gap-4 mt-3">
                      <div className="bg-info/10  rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-info ">{retrievals ?? 'Unavailable'}</p>
                        <p className="text-xs text-info dark:text-info">Retrievals</p>
                      </div>
                      <div className="bg-success/10  rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-success ">
                          {(helpfulRate !== undefined && helpfulRate !== null) ? `${(helpfulRate * 100).toFixed(0)}%` : '—'}
                        </p>
                        <p className="text-xs text-success dark:text-success">Helpful Rate</p>
                      </div>
                      <div className="bg-primary/5 rounded-lg p-3 text-center">
                        <p className="text-sm font-medium text-primary mt-1.5">{lastRetrieved ? formatDate(lastRetrieved) : '—'}</p>
                        <p className="text-xs text-primary/80 mt-1">Last Retrieved</p>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>

        <DialogFooter className={editing ? "hidden" : "flex-col sm:flex-row gap-2 border-t pt-4 [&_button]:min-h-11"}>
          {/* Status-based actions - All actions consolidated in footer */}
          <div className="flex min-h-10 flex-wrap gap-2 w-full sm:w-auto justify-end">
            {/* Draft submit action (Cancel/Save are rendered above while editing) */}
            {!error && !loading && isDraft && canUpload && !editing && (
              <Button onClick={() => handleWorkflowAction('submit')} className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Send className="w-4 h-4 mr-1" /> Submit for Review
              </Button>
            )}
            {/* Review actions */}
            {!error && !loading && isPendingReview && canApprove && (
              <>
                <Button variant="outline" onClick={() => handleWorkflowAction('reject')} className="text-warning border-warning/40">
                  <XCircle className="w-4 h-4 mr-1" /> Reject
                </Button>
                <Button onClick={() => handleWorkflowAction('approve')} className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <CheckCircle className="w-4 h-4 mr-1" /> Approve
                </Button>
              </>
            )}
            {/* Approved actions */}
            {!error && !loading && isApproved && canPublish && (
              <Button onClick={() => handleWorkflowAction('publish')} className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Upload className="w-4 h-4 mr-1" /> Start Ingestion
              </Button>
            )}
            {/* Ingestion failed or index missing/untrustworthy */}
            {!error && !loading && isPublished && !editing && canRetryIngestion(data) && canPublish && (
              <Button onClick={() => handleWorkflowAction('retry-ingestion')} variant="outline" className="text-warning border-warning/40">
                <RefreshCw className="w-4 h-4 mr-1" /> Retry Ingestion
              </Button>
            )}
            {/* Live actions */}
            {!error && !loading && isLive && !editing && canDelete && (
              <Button onClick={() => handleWorkflowAction('archive')} variant="outline" className="text-warning border-warning/40">
                <Archive className="w-4 h-4 mr-1" /> Archive
              </Button>
            )}
            {/* Archived actions */}
            {!error && !loading && isArchived && canUpload && (
              <Button onClick={() => handleWorkflowAction('restore')} className="bg-primary text-primary-foreground hover:bg-primary/90">
                <RefreshCw className="w-4 h-4 mr-1" /> Restore to Draft
              </Button>
            )}
            {/* Close button — always shown; requestClose prompts to discard
                unsaved metadata edits before closing */}
            <Button variant="outline" onClick={requestClose}>Close</Button>
          </div>
        </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}



// Bulk Upload Dialog Component
// EIP-570: distinguishes "the user stopped this" from a genuine upload
// failure, so a cancelled file is not reported back to them as an error.
class BulkUploadCancelled extends Error {
  constructor() {
    super('Upload cancelled');
    this.name = 'BulkUploadCancelled';
  }
}

function BulkUploadDialog({ open, onOpenChange, onSuccess, currentFolderId, destinationLabel = 'Unfiled', canManagePlan, onCloseAutoFocus }) {
  const [ConfirmBulkElement, confirmBulk] = useConfirm();
  const [files, setFiles] = useState([]);
  const [folderStructure, setFolderStructure] = useState({}); // path -> files mapping
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [wizardStep, setWizardStep] = useState(0);
  const [scanningFiles, setScanningFiles] = useState(false);
  const [progress, setProgress] = useState({ 
    currentFile: 0, 
    totalFiles: 0, 
    currentFileProgress: 0,
    uploadedFiles: [],
    failedFiles: [],
    // New: Upload estimation fields
    startTime: null,
    bytesUploaded: 0,
    totalBytes: 0,
    estimatedTimeRemaining: null,
    uploadSpeed: 0,
    currentStage: 'upload',
  });
  const [uploadMode, setUploadMode] = useState('files'); // 'files' or 'folder'
  const [incompleteUploads, setIncompleteUploads] = useState([]);
  const [resumingUpload, setResumingUpload] = useState(null);
  // New: Queue management state
  const [fileQueue, setFileQueue] = useState([]); // Enhanced file list with status
  const [duplicates, setDuplicates] = useState([]); // Detected duplicates
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [analyzeWithEva, setAnalyzeWithEva] = useState(true);
  const [kbTokenBucket, setKbTokenBucket] = useState(null);
  const pauseRef = useRef(false);
  // EIP-570. `abortRef` used to live here as a Set of "cancelled file indices"
  // that nothing ever added to, so it read as a cancel mechanism in review
  // while always being empty. Replaced by a flag the loops actually check and
  // an AbortController for the request in flight.
  const cancelRef = useRef(false);
  const abortControllerRef = useRef(null);
  const activeChunkedUploadRef = useRef(null); // upload_id, for server-side cleanup
  const createdDocsRef = useRef([]); // documents this batch created, for the cleanup offer
  const [cancelling, setCancelling] = useState(false)
  const serverStageTimersRef = useRef([]);
  const currentUploadIndexRef = useRef(-1);
  const tokenPollCancelledRef = useRef(false); // Stop token polling when the wizard closes
  const [allowedFileTypes, setAllowedFileTypes] = useState({ accept_string: '.pdf', allowed: ['pdf'] });

  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  // Fetch incomplete uploads when dialog opens
  useEffect(() => {
    if (open) {
      fetchIncompleteUploads();
      api.get('/kb/allowed-file-types').then(res => setAllowedFileTypes(res.data)).catch(() => {});
      api.get('/usage')
        .then(res => {
          const bucket = res.data?.token_buckets?.knowledge_base;
          setKbTokenBucket(bucket || null);
          if (bucket && bucket.remaining !== null && Number(bucket.remaining || 0) <= 0) {
            setAnalyzeWithEva(false);
          }
        })
        .catch(() => setKbTokenBucket(null));
    }
  }, [open]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setFiles([]);
      setFileQueue([]);
      setFolderStructure({});
      setUploadResults(null);
      setWizardStep(0);
      setScanningFiles(false);
      setDuplicates([]);
      setShowDuplicateDialog(false);
      setIsPaused(false);
      setAnalyzeWithEva(true);
      setKbTokenBucket(null);
      pauseRef.current = false;
      cancelRef.current = false;
      abortControllerRef.current = null;
      activeChunkedUploadRef.current = null;
      createdDocsRef.current = [];
      setCancelling(false);
      currentUploadIndexRef.current = -1;
      setProgress({ 
        currentFile: 0, totalFiles: 0, currentFileProgress: 0, 
        uploadedFiles: [], failedFiles: [],
        startTime: null, bytesUploaded: 0, totalBytes: 0,
        estimatedTimeRemaining: null, uploadSpeed: 0, currentStage: 'upload',
      });
      setResumingUpload(null);
    }
  }, [open]);

  const clearServerStageTimers = () => {
    serverStageTimersRef.current.forEach(timer => clearTimeout(timer));
    serverStageTimersRef.current = [];
  };

  const setPipelineStage = (stage) => {
    setProgress(prev => ({ ...prev, currentStage: stage }));
    setFileQueue(prev => prev.map((file, idx) => (
      idx === currentUploadIndexRef.current ? { ...file, stage } : file
    )));
  };

  const scheduleServerPipelineStages = () => {
    clearServerStageTimers();
    setPipelineStage('validation');
    serverStageTimersRef.current = [
      setTimeout(() => setPipelineStage('preparation'), 350),
      setTimeout(() => setPipelineStage('extraction'), 800),
    ];
  };

  const fetchIncompleteUploads = async () => {
    try {
      const response = await api.get('/kb/documents/chunked/incomplete');
      setIncompleteUploads(response.data?.incomplete_uploads || []);
    } catch (err) {
      console.error('Failed to fetch incomplete uploads:', err);
    }
  };

  // Check for duplicates before upload
  const checkForDuplicates = async (fileList) => {
    try {
      const filenames = fileList.map(f => f.file.name);
      const response = await api.post('/kb/documents/check-duplicates', {
        filenames,
        folder_id: null  // global check across entire KB
      });
      
      if (response.data.duplicate_count > 0) {
        setDuplicates(response.data.duplicates);
        setShowDuplicateDialog(true);
        return response.data.duplicates;
      }
      return [];
    } catch (err) {
      console.error('Failed to check duplicates:', err);
      return [];
    }
  };

  const handleDuplicateAction = (action) => {
    // action: 'skip', 'copy', 'keep_both'
    const duplicateFilenames = new Set(duplicates.map(d => d.filename));
    
    if (action === 'skip') {
      // Remove duplicates from the file list
      setFiles(prev => prev.filter(f => !duplicateFilenames.has(f.file.name)));
      setFileQueue(prev => prev.filter(f => !duplicateFilenames.has(f.file.name)));
    } else if (action === 'keep_both') {
      // Rename duplicates by adding timestamp
      const timestamp = Date.now();
      const rename = f => {
        if (!duplicateFilenames.has(f.file.name)) return f;
        const dot = f.file.name.lastIndexOf('.');
        const newName = `${f.file.name.slice(0, dot)}_${timestamp}${f.file.name.slice(dot)}`;
        const parent = f.relativePath?.includes('/') ? f.relativePath.slice(0, f.relativePath.lastIndexOf('/') + 1) : '';
        return { ...f, file: new window.File([f.file], newName, { type: f.file.type }), relativePath: parent + newName };
      };
      setFiles(prev => prev.map(rename));
      setFileQueue(prev => prev.map(rename));
    }
    // Uploading another copy does not replace the existing document.

    setShowDuplicateDialog(false);
    setDuplicates([]);
  };

  // Format time remaining
  const formatTimeRemaining = (seconds) => {
    if (!seconds || seconds < 0) return 'Calculating...';
    if (seconds < 60) return `${Math.ceil(seconds)}s remaining`;
    if (seconds < 3600) return `${Math.ceil(seconds / 60)}m remaining`;
    return `${Math.floor(seconds / 3600)}h ${Math.ceil((seconds % 3600) / 60)}m remaining`;
  };

  // Format upload speed
  const formatSpeed = (bytesPerSecond) => {
    if (!bytesPerSecond) return '';
    if (bytesPerSecond < 1024) return `${bytesPerSecond.toFixed(0)} B/s`;
    if (bytesPerSecond < 1024 * 1024) return `${(bytesPerSecond / 1024).toFixed(1)} KB/s`;
    return `${(bytesPerSecond / (1024 * 1024)).toFixed(1)} MB/s`;
  };

  // Auto-retry wrapper for chunk uploads
  const uploadChunkWithRetry = async (url, formData, maxRetries = 3) => {
    let lastError;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await api.post(url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response;
      } catch (err) {
        lastError = err;
        if (attempt < maxRetries) {
          // Exponential backoff: 1s, 2s, 4s
          const delay = Math.pow(2, attempt - 1) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    throw lastError;
  };

  const handleResumeUpload = async (upload) => {
    setResumingUpload(upload);
    setUploading(true);
    setProgress({
      currentFile: 1,
      totalFiles: 1,
      currentFileProgress: upload.progress_percent,
      uploadedFiles: [],
      failedFiles: [],
      currentStage: 'upload',
    });

    try {
      // Get resume info with missing chunks
      const resumeResponse = await api.post(`/kb/documents/chunked/${upload.upload_id}/resume`);
      const { missing_chunks, total_chunks, filename } = resumeResponse.data;

      toast.info('Resuming Upload', { description: `Resuming ${filename}: ${missing_chunks.length} chunks remaining` });

      // We need the original file to resume - prompt user to re-select it
      // For now, show instructions
      toast.info('File Required', { description: `Please select the original file "${filename}" to continue upload.` });

      // Store resume state for when user selects file
      setResumingUpload({
        ...upload,
        missing_chunks,
        total_chunks,
        awaiting_file: true
      });
      setUploading(false);

    } catch (err) {
      toast.error('Resume Failed', { description: parseErrorDetail(err).message });
      setUploading(false);
      setResumingUpload(null);
    }
  };

  const handleResumeFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile || !resumingUpload) return;

    // Verify file name matches
    if (selectedFile.name !== resumingUpload.filename) {
      toast.error('Wrong File', { description: `Expected "${resumingUpload.filename}" but got "${selectedFile.name}"` });
      return;
    }

    // Verify file size matches
    if (selectedFile.size !== resumingUpload.file_size) {
      toast.error('File Size Mismatch', { description: 'The selected file has a different size. Please select the original file.' });
      return;
    }

    setUploading(true);
    const CHUNK_SIZE = 1024 * 1024; // 1MB chunks

    try {
      const { upload_id, missing_chunks, total_chunks } = resumingUpload;
      let completedChunks = total_chunks - missing_chunks.length;

      // Upload only missing chunks
      for (let i = 0; i < missing_chunks.length; i++) {
        const chunkIndex = missing_chunks[i];
        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, selectedFile.size);
        const chunk = selectedFile.slice(start, end);

        const chunkFormData = new FormData();
        chunkFormData.append('chunk_index', chunkIndex);
        chunkFormData.append('chunk', chunk);

        await api.post(`/kb/documents/chunked/${upload_id}/chunk`, chunkFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        completedChunks++;
        setProgress(prev => ({
          ...prev,
          currentFileProgress: Math.round((completedChunks / total_chunks) * 100),
          currentStage: 'upload',
        }));
      }

      // Complete the upload
      if (analyzeWithEva) {
        scheduleServerPipelineStages();
      } else {
        setPipelineStage('validation');
      }
      const completeFormData = new FormData();
      completeFormData.append('analyze_with_eva', String(analyzeWithEva));
      const completeResponse = await api.post(`/kb/documents/chunked/${upload_id}/complete`, completeFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setUploadResults({
        uploaded_count: 1,
        failed_count: 0,
        documents: [completeResponse.data],
        errors: [],
        folders_created: []
      });
      setWizardStep(3);

      toast.success('Upload Resumed Successfully', { description: `${resumingUpload.filename} has been uploaded.` });

      // Refresh incomplete uploads list
      fetchIncompleteUploads();

    } catch (err) {
      toast.error('Resume Upload Failed', { description: parseErrorDetail(err).message });
    } finally {
      setUploading(false);
      setResumingUpload(null);
    }
  };

  const handleCancelIncomplete = async (uploadId) => {
    try {
      await api.delete(`/kb/documents/chunked/${uploadId}`);
      toast.success('Upload Cancelled', { description: 'Incomplete upload has been removed.' });
      fetchIncompleteUploads();
    } catch (err) {
      toast.error('Cancel Failed', { description: err.response?.data?.detail || err.message });
    }
  };

  const handleFilesSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    setScanningFiles(true);
    setUploadResults(null);
    await new Promise(resolve => setTimeout(resolve, 0));
    
    // Filter valid file types using the dynamic allowedFileTypes from API
    const validExts = (allowedFileTypes.allowed || ['pdf']).map(ext => `.${ext.toLowerCase()}`);
    const validFiles = selectedFiles.filter(f => 
      validExts.some(ext => f.name.toLowerCase().endsWith(ext))
    );
    
    // For regular file selection, no folder structure
    const filesWithPaths = validFiles.map((f, idx) => ({
      file: f,
      relativePath: f.name,
      folderPath: '',
      status: 'queued', // queued, uploading, paused, completed, failed, cancelled
      index: idx
    }));
    setFiles(filesWithPaths);
    setFileQueue(filesWithPaths);
    setFolderStructure({});
    setUploadMode('files');
    setResumingUpload(null);
    setWizardStep(filesWithPaths.length > 0 ? 1 : 0);
    
    // Notify user if files were skipped
    if (validFiles.length < selectedFiles.length) {
      toast.info('Some files skipped', { description: `${selectedFiles.length - validFiles.length} file(s) were skipped (unsupported format). Allowed: ${(allowedFileTypes.allowed || ['pdf']).map(t => t.toUpperCase()).join(', ')}` });
    }
    
    // Check for duplicates
    if (filesWithPaths.length > 0) {
      await checkForDuplicates(filesWithPaths);
    }
    setScanningFiles(false);
  };

  const handleFolderSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;
    setScanningFiles(true);
    setUploadResults(null);
    await new Promise(resolve => setTimeout(resolve, 0));

    // Filter valid file types using the dynamic allowedFileTypes from API
    const validExts = (allowedFileTypes.allowed || ['pdf']).map(ext => `.${ext.toLowerCase()}`);
    const validFiles = selectedFiles.filter(f => 
      validExts.some(ext => f.name.toLowerCase().endsWith(ext))
    );

    // Build folder structure from webkitRelativePath
    const structure = {};
    const filesWithPaths = validFiles.map((f, idx) => {
      const relativePath = f.webkitRelativePath || f.name;
      const parts = relativePath.split('/');
      const folderPath = parts.slice(0, -1).join('/');
      
      if (folderPath) {
        if (!structure[folderPath]) {
          structure[folderPath] = [];
        }
        structure[folderPath].push(f.name);
      }
      
      return {
        file: f,
        relativePath: relativePath,
        folderPath: folderPath,
        status: 'queued',
        index: idx
      };
    });

    setFiles(filesWithPaths);
    setFileQueue(filesWithPaths);
    setFolderStructure(structure);
    setUploadMode('folder');
    setWizardStep(filesWithPaths.length > 0 ? 1 : 0);

    if (validFiles.length < selectedFiles.length) {
      toast.info('Some files skipped', { description: `${selectedFiles.length - validFiles.length} file(s) were skipped (unsupported format). Allowed: ${(allowedFileTypes.allowed || ['pdf']).map(t => t.toUpperCase()).join(', ')}` });
    }
    
    // Check for duplicates
    if (filesWithPaths.length > 0) {
      await checkForDuplicates(filesWithPaths);
    }
    setScanningFiles(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    // Capture everything we need from the DataTransfer synchronously. Once the
    // drop event finishes dispatching, the browser clears the drag data store,
    // so any await before reading items/files leaves them empty and the drop
    // silently does nothing (EIP-289).
    const items = e.dataTransfer.items;
    const folderEntry =
      items && items.length > 0 && items[0].webkitGetAsEntry
        ? items[0].webkitGetAsEntry()
        : null;
    const allDroppedFiles = Array.from(e.dataTransfer.files);

    setScanningFiles(true);
    await new Promise(resolve => setTimeout(resolve, 0));

    // Handle folder drop (directory entry captured above, before the await)
    if (folderEntry && folderEntry.isDirectory) {
      await processDroppedFolder(folderEntry);
      setScanningFiles(false);
      return;
    }

    // Regular file drop - use dynamic allowedFileTypes from API
    const validExts = (allowedFileTypes.allowed || ['pdf']).map(ext => `.${ext.toLowerCase()}`);
    const droppedFiles = allDroppedFiles.filter(f =>
      validExts.some(ext => f.name.toLowerCase().endsWith(ext))
    );
    
    // Notify user if files were skipped
    if (droppedFiles.length < allDroppedFiles.length) {
      toast.info('Some files skipped', { description: `${allDroppedFiles.length - droppedFiles.length} file(s) were skipped (unsupported format). Allowed: ${(allowedFileTypes.allowed || ['pdf']).map(t => t.toUpperCase()).join(', ')}` });
    }
    
    const filesWithPaths = droppedFiles.map((f, idx) => ({
      file: f,
      relativePath: f.name,
      folderPath: '',
      status: 'queued',
      index: files.length + idx
    }));
    const newFiles = [...files, ...filesWithPaths];
    setFiles(newFiles);
    setFileQueue(newFiles);
    setUploadMode('files');
    setWizardStep(newFiles.length > 0 ? 1 : 0);
    
    // Check for duplicates
    if (filesWithPaths.length > 0) {
      await checkForDuplicates(filesWithPaths);
    }
    setScanningFiles(false);
  };

  const processDroppedFolder = async (entry) => {
    const allFiles = [];
    const structure = {};
    let skippedCount = 0;

    // Use dynamic allowedFileTypes from API
    const validExts = (allowedFileTypes.allowed || ['pdf']).map(ext => `.${ext.toLowerCase()}`);

    const readDirectory = async (dirEntry, path = '') => {
      const reader = dirEntry.createReader();
      const entries = [];
      // Browsers may return only a portion of a directory per call.
      while (true) {
        const batch = await new Promise((resolve, reject) => reader.readEntries(resolve, reject));
        if (!batch.length) break;
        entries.push(...batch);
      }

      for (const entry of entries) {
        const entryPath = path ? `${path}/${entry.name}` : entry.name;
        
        if (entry.isDirectory) {
          await readDirectory(entry, entryPath);
        } else if (entry.isFile) {
          const file = await new Promise((resolve) => entry.file(resolve));
          if (validExts.some(ext => file.name.toLowerCase().endsWith(ext))) {
            allFiles.push({
              file: file,
              relativePath: entryPath,
              folderPath: path
            });
            
            if (path) {
              if (!structure[path]) structure[path] = [];
              structure[path].push(file.name);
            }
          } else {
            skippedCount++;
          }
        }
      }
    };

    await readDirectory(entry, entry.name);
    setFiles(allFiles);
    setFileQueue(allFiles.map((item, index) => ({ ...item, status: 'queued', index })));
    setFolderStructure(structure);
    setUploadMode('folder');
    setWizardStep(allFiles.length > 0 ? 1 : 0);
    
    // Notify user if files were skipped
    if (skippedCount > 0) {
      toast.info('Some files skipped', { description: `${skippedCount} file(s) were skipped (unsupported format). Allowed: ${(allowedFileTypes.allowed || ['pdf']).map(t => t.toUpperCase()).join(', ')}` });
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setFileQueue(prev => prev.filter((_, i) => i !== index));
  };

  // Pause/Resume all uploads
  const rememberCreatedDoc = (doc) => {
    if (doc?.id) {
      createdDocsRef.current = [...createdDocsRef.current, { id: doc.id, title: doc.title }];
    }
  };

  // EIP-570. Deleting is how a half-ingested document is actually stopped:
  // ingestion runs as a FastAPI BackgroundTask on the serving instance with no
  // job record and no cancel endpoint, so there is no task handle to call off.
  // The delete path already handles the race correctly -- the pipeline's
  // terminal UPDATE matches zero rows, notices the row is gone, and cleans up
  // the text object and page rows it just wrote.
  const deleteCreatedDocs = async (docs) => {
    const results = await Promise.allSettled(
      docs.map((doc) => api.delete(`/kb/documents/${doc.id}`))
    );
    const removed = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.length - removed;

    if (removed > 0) {
      toast.success(`${removed} Document${removed === 1 ? '' : 's'} Removed`);
      onSuccess();
    }
    if (failed > 0) {
      // Bulk upload is open to analyst and approver; DELETE /kb/documents/{id}
      // is admin-only. Say so plainly rather than failing silently.
      toast.error(`${failed} Document${failed === 1 ? '' : 's'} Could Not Be Removed`, {
        description: 'Removing documents requires an admin. Ask an administrator to delete them.',
      });
    }
  };

  // Stop the batch. Sets the flag both loops check, aborts the request in
  // flight, and offers to remove whatever was already created -- which is the
  // credit-preservation half of the ticket, since every completed upload has
  // already dispatched ingestion.
  const cancelBulkUpload = () => {
    cancelRef.current = true;
    pauseRef.current = false;
    setIsPaused(false);
    setCancelling(true);
    tokenPollCancelledRef.current = true;

    try {
      abortControllerRef.current?.abort();
    } catch (err) {
      console.error('Failed to abort in-flight upload:', err);
    }

    const created = createdDocsRef.current;
    if (created.length > 0) {
      toast.info(
        `Import stopped. ${created.length} document${created.length === 1 ? '' : 's'} had already been created.`,
        {
          duration: 10000,
          description: 'Their analysis has already started and used credits.',
          action: {
            label: 'Remove them',
            onClick: () => deleteCreatedDocs(created),
          },
        }
      );
    } else {
      toast.info('Import stopped. No documents were created.');
    }
  };

  const togglePause = () => {
    pauseRef.current = !pauseRef.current;
    setIsPaused(pauseRef.current);
    
    if (pauseRef.current) {
      toast.info('Upload Paused', { description: 'Click Resume to continue.' });
    } else {
      toast.info('Upload Resumed');
    }
  };

  const handleBulkUpload = async (e) => {
    if (e) e.preventDefault();
    if (files.length === 0 || uploading) return;
    if (analyzeWithEva && kbTokensAtLimit) {
      showLimitError({
        message: 'Knowledge base usage is at limit. Upload as drafts or manage your plan before asking Eva to analyze documents.',
        upgrade_url: '/settings/billing',
        cta_label: 'Manage Plan',
      }, null, 'Knowledge base usage is at limit', {
        canUseCta: canManagePlan,
      });
      return;
    }
    
    // Calculate total bytes for estimation
    const totalBytes = files.reduce((sum, f) => sum + f.file.size, 0);
    
    setUploading(true);
    setWizardStep(2);
    setIsPaused(false);
    pauseRef.current = false;
    cancelRef.current = false;
    setCancelling(false);
    createdDocsRef.current = [];
    tokenPollCancelledRef.current = false;
    
    setProgress({ 
      currentFile: 0, 
      totalFiles: files.length, 
      currentFileProgress: 0,
      uploadedFiles: [],
      failedFiles: [],
      startTime: Date.now(),
      bytesUploaded: 0,
      totalBytes: totalBytes,
      estimatedTimeRemaining: null,
      uploadSpeed: 0,
      currentStage: 'upload',
    });

    const CHUNK_SIZE = 1024 * 1024; // 1MB chunks
    const uploadedDocs = [];
    const failedUploads = [];
    let bytesUploaded = 0;
    const startTime = Date.now();

    for (let i = 0; i < files.length; i++) {
      // EIP-570: stop the batch outright. Previously the only exit from this
      // loop was running out of files, so closing the modal left it uploading
      // invisibly.
      if (cancelRef.current) break;

      // Wait while paused -- but a cancel must break the wait, or closing a
      // PAUSED dialog parks this invocation in a 2-per-second timer for the
      // page's lifetime, holding every File handle, and reopening the dialog
      // resumes the zombie to race the new run.
      while (pauseRef.current && !cancelRef.current) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      if (cancelRef.current) break;
      
      const { file, relativePath } = files[i];
      currentUploadIndexRef.current = i;
      
      // Update queue status
      setFileQueue(prev => prev.map((f, idx) => 
        idx === i ? { ...f, status: 'uploading', stage: 'upload' } : f
      ));
      
      setProgress(prev => ({
        ...prev,
        currentFile: i + 1,
        currentFileProgress: 0,
        currentStage: 'upload',
      }));

      try {
        let uploadedDoc;
        if (file.size > CHUNK_SIZE * 2) {
          // Use chunked upload for large files with estimation
          uploadedDoc = await uploadFileChunked(file, relativePath, CHUNK_SIZE, (chunkProgress, _chunkBytes) => {
            const currentBytesUploaded = bytesUploaded + (file.size * chunkProgress / 100);
            const elapsedSeconds = (Date.now() - startTime) / 1000;
            const speed = currentBytesUploaded / elapsedSeconds;
            const remainingBytes = totalBytes - currentBytesUploaded;
            const estimatedSeconds = speed > 0 ? remainingBytes / speed : null;
            
            setProgress(prev => ({ 
              ...prev, 
              currentFileProgress: chunkProgress,
              bytesUploaded: currentBytesUploaded,
              uploadSpeed: speed,
              estimatedTimeRemaining: estimatedSeconds
            }));
          });
          uploadedDocs.push(uploadedDoc);
          rememberCreatedDoc(uploadedDoc);
          bytesUploaded += file.size;
        } else {
          // Use regular upload for small files
          uploadedDoc = await uploadFileRegular(file, relativePath);
          uploadedDocs.push(uploadedDoc);
          rememberCreatedDoc(uploadedDoc);
          bytesUploaded += file.size;
          
          // Update estimation after small file
          const elapsedSeconds = (Date.now() - startTime) / 1000;
          const speed = bytesUploaded / elapsedSeconds;
          const remainingBytes = totalBytes - bytesUploaded;
          setProgress(prev => ({
            ...prev,
            bytesUploaded,
            uploadSpeed: speed,
            estimatedTimeRemaining: speed > 0 ? remainingBytes / speed : null
          }));
        }
        
        // Update queue and progress status. Token usage is null while server-side
        // analysis is pending — pollDocumentTokens fills it in later (EIP-159).
        setFileQueue(prev => prev.map((f, idx) =>
          idx === i ? {
            ...f,
            status: 'completed',
            stage: analyzeWithEva ? 'complete' : 'draft',
            document: uploadedDoc,
            kbTokensUsed: uploadedDoc?.kb_tokens_used ?? null,
            llmKbTokensUsed: uploadedDoc?.llm_kb_tokens_used ?? null,
          } : f
        ));

        if (uploadedDoc?.id && uploadedDoc.kb_tokens_used == null) {
          pollDocumentTokens(uploadedDoc);
        }
        
        setProgress(prev => ({
          ...prev,
          currentFileProgress: 100,
          currentStage: 'extraction',
          uploadedFiles: [...prev.uploadedFiles, { filename: file.name, status: 'success' }]
        }));
        await refreshParentUsage();
        
      } catch (err) {
        if (err instanceof BulkUploadCancelled || cancelRef.current) {
          // Stopped on purpose. Don't record it as a failed upload, or the
          // summary tells the user their import broke when they cancelled it.
          setFileQueue(prev => prev.map((f, idx) =>
            idx === i ? { ...f, status: 'cancelled', stage: 'cancelled' } : f
          ));
          break;
        }
        console.error(`Failed to upload ${file.name}:`, err);
        const uploadError = formatBulkUploadError(err);
        failedUploads.push({ filename: file.name, error: uploadError.message, type: uploadError.type, label: uploadError.label });
        
        setFileQueue(prev => prev.map((f, idx) => 
          idx === i ? { ...f, status: 'failed', stage: 'failed' } : f
        ));
        
        setProgress(prev => ({
          ...prev,
          failedFiles: [...prev.failedFiles, { filename: file.name, error: uploadError.message }]
        }));
      }
    }

    setUploadResults({
      uploaded_count: uploadedDocs.length,
      failed_count: failedUploads.length,
      documents: uploadedDocs,
      errors: failedUploads,
      folders_created: [...new Set(uploadedDocs.map(d => d.relative_path?.includes('/') ? d.relative_path.slice(0, d.relative_path.lastIndexOf('/')) : '').filter(Boolean))]
    });
    setWizardStep(3);
    await refreshParentUsage();

    setUploading(false);

    if (uploadedDocs.length > 0) {
      toast.success(`${uploadedDocs.length} Documents Uploaded`, {
        description: analyzeWithEva
          ? 'Drafts saved. Eva analysis continues in the background.'
          : 'Documents were created as drafts without Eva analysis.',
      });
    }
    if (failedUploads.length > 0) {
      const tokenLimitFailure = failedUploads.find(item => item.type === 'token_limit');
      const planLimitFailure = failedUploads.find(item => item.type === 'plan_limit');
      const failureTitle = tokenLimitFailure
        ? 'Knowledge base usage limit reached'
        : planLimitFailure
          ? 'Page limit exceeded'
          : `${failedUploads.length} Upload${failedUploads.length === 1 ? '' : 's'} Failed`;
      toast.error(failureTitle, {
        description: tokenLimitFailure?.error || planLimitFailure?.error || failedUploads[0]?.error || 'Review the upload results for details.',
      });
    }
  };

  // EIP-159: Eva analysis runs server-side after the upload response, so the
  // upload payload carries kb_tokens_used=null. Poll the document until
  // ingestion settles, then surface the real KB usage in the wizard tables.
  const pollDocumentTokens = async (uploadedDoc) => {
    const docId = uploadedDoc.id;
    for (let attempt = 0; attempt < 40; attempt += 1) {
      if (tokenPollCancelledRef.current) return;
      await new Promise(resolve => setTimeout(resolve, 3000));
      try {
        const { data } = await api.get(`/kb/documents/${docId}`);
        if (!['complete', 'failed', 'indexed'].includes(data.ingestion_status)) continue;
        const kbTokens = Number(data.kb_tokens_used || 0);
        const llmKbTokens = Number(data.llm_kb_tokens_used || 0);
        // Keep the pre-results snapshot fresh too: uploadedDoc is the same
        // object collected into uploadResults when the upload loop finishes.
        uploadedDoc.kb_tokens_used = kbTokens;
        uploadedDoc.llm_kb_tokens_used = llmKbTokens;
        setFileQueue(prev => prev.map(f => f.document?.id === docId
          ? { ...f, kbTokensUsed: kbTokens, llmKbTokensUsed: llmKbTokens }
          : f
        ));
        setUploadResults(prev => prev ? {
          ...prev,
          documents: prev.documents.map(d => d.id === docId
            ? { ...d, kb_tokens_used: kbTokens, llm_kb_tokens_used: llmKbTokens }
            : d),
        } : prev);
        return;
      } catch (err) {
        if (err.response?.status === 404) return; // Document deleted meanwhile
        // Transient error — keep polling
      }
    }
  };

  const uploadFileRegular = async (file, relativePath) => {
    const formData = new FormData();
    formData.append('files', file, file.name);
    if (currentFolderId && currentFolderId !== 'unfiled') {
      formData.append('base_folder_id', currentFolderId);
    }
    formData.append('analyze_with_eva', String(analyzeWithEva));
    
    // Build folder structure info for this file
    if (relativePath) {
      formData.append('relative_path', relativePath);
    }
    if (relativePath && relativePath.includes('/')) {
      formData.append('folder_structure', JSON.stringify([{ filename: file.name, relativePath }]));
    }

    // EIP-570: without a signal, "cancel" could only stop the loop BETWEEN
    // files -- the one in flight still completed, created a document and
    // dispatched its ingestion.
    abortControllerRef.current = new AbortController();
    const response = await api.post('/kb/documents/bulk-with-folders', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      signal: abortControllerRef.current.signal,
      onUploadProgress: (event) => {
        if (!event.total) return;
        const percent = Math.round((event.loaded / event.total) * 100);
        setProgress(prev => ({ ...prev, currentFileProgress: percent, currentStage: 'upload' }));
        if (percent >= 100 && analyzeWithEva) {
          scheduleServerPipelineStages();
        } else if (percent >= 100) {
          setPipelineStage('validation');
        }
      },
    });

    if (response.data.documents && response.data.documents.length > 0) {
      return response.data.documents[0];
    }
    if (response.data.errors && response.data.errors.length > 0) {
      const firstError = response.data.errors[0];
      const bulkError = new Error(response.data.errors.map(err => err.error || err.message).join(', '));
      bulkError.response = {
        data: {
          detail: firstError.detail || firstError.error || firstError.message,
        },
      };
      throw bulkError;
    }
    throw new Error('Upload completed without creating a document. Please try again.');
  };

  const uploadFileChunked = async (file, relativePath, chunkSize, onProgress) => {
    // Initialize chunked upload
    const totalChunks = Math.ceil(file.size / chunkSize);
    
    const initFormData = new FormData();
    initFormData.append('filename', file.name);
    initFormData.append('file_size', String(file.size));
    initFormData.append('total_chunks', String(totalChunks));
    if (currentFolderId && currentFolderId !== 'unfiled') {
      initFormData.append('folder_id', currentFolderId);
    }
    initFormData.append('analyze_with_eva', String(analyzeWithEva));
    if (relativePath) {
      initFormData.append('relative_path', relativePath);
    }

    const initResponse = await api.post('/kb/documents/chunked/init', initFormData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const uploadId = initResponse.data.upload_id;
    activeChunkedUploadRef.current = uploadId;

    // Upload chunks with auto-retry
    for (let i = 0; i < totalChunks; i++) {
      while (pauseRef.current && !cancelRef.current) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      // EIP-570: abandoning mid-sequence would strand the chunk directory and
      // its stg_chunked_uploads row, so hand the server its own cancel. That
      // endpoint admits analyst and approver too, unlike the document delete,
      // so this half of the cleanup works for every role that can bulk import.
      if (cancelRef.current) {
        try {
          await api.delete(`/kb/documents/chunked/${uploadId}`);
        } catch (cleanupErr) {
          console.error('Failed to clean up cancelled chunked upload:', cleanupErr);
        }
        activeChunkedUploadRef.current = null;
        throw new BulkUploadCancelled();
      }
      
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);

      const chunkFormData = new FormData();
      chunkFormData.append('chunk_index', i);
      chunkFormData.append('chunk', chunk);

      // Use auto-retry wrapper (3 attempts with exponential backoff)
      await uploadChunkWithRetry(`/kb/documents/chunked/${uploadId}/chunk`, chunkFormData);

      const progressPercent = Math.round(((i + 1) / totalChunks) * 100);
      const bytesUploaded = Math.min((i + 1) * chunkSize, file.size);
      onProgress(progressPercent, bytesUploaded);
    }

    // EIP-570: cancelling during the LAST chunk exits the loop normally, so
    // without this the request below would still complete, create a document
    // and dispatch its ingestion -- exactly what the user just stopped.
    if (cancelRef.current) {
      try {
        await api.delete(`/kb/documents/chunked/${uploadId}`);
      } catch (cleanupErr) {
        console.error('Failed to clean up cancelled chunked upload:', cleanupErr);
      }
      activeChunkedUploadRef.current = null;
      throw new BulkUploadCancelled();
    }

    // Complete upload
    if (analyzeWithEva) {
      scheduleServerPipelineStages();
    } else {
      setPipelineStage('validation');
    }
    const completeFormData = new FormData();
    completeFormData.append('analyze_with_eva', String(analyzeWithEva));
    const completeResponse = await api.post(`/kb/documents/chunked/${uploadId}/complete`, completeFormData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    activeChunkedUploadRef.current = null;
    return completeResponse.data;
  };

  const handleClose = async () => {
    if (!uploading && !uploadResults && files.length && !(await confirmBulk({
      title: 'Discard selected uploads?', description: 'Your selected files have not been uploaded.', confirmLabel: 'Discard Uploads', variant: 'warning',
    }))) return;
    // EIP-570: this used to clear the form and leave handleBulkUpload running.
    // Worse, it never reset pauseRef, so closing a PAUSED dialog parked the
    // loop in a 500ms timer forever, and reopening reset pauseRef to false and
    // RESUMED the zombie to race a fresh run against a stale files array.
    if (uploading) {
      cancelBulkUpload();
    }
    pauseRef.current = false;
    setIsPaused(false);
    setUploading(false);
    setFiles([]);
    setFolderStructure({});
    setUploadResults(null);
    setWizardStep(0);
    setScanningFiles(false);
    clearServerStageTimers();
    currentUploadIndexRef.current = -1;
    tokenPollCancelledRef.current = true;
    setProgress({ currentFile: 0, totalFiles: 0, currentFileProgress: 0, uploadedFiles: [], failedFiles: [], currentStage: 'upload' });
    onOpenChange(false);
    if (uploadResults?.uploaded_count > 0) {
      onSuccess();
    }
  };

  // Calculate overall progress percentage
  const overallProgress = progress.totalFiles > 0 
    ? Math.round(((progress.currentFile - 1 + progress.currentFileProgress / 100) / progress.totalFiles) * 100)
    : 0;

  // Group files by folder for display
  const groupedFiles = useMemo(() => {
    const groups = { '': [] };
    files.forEach((f, idx) => {
      const folder = f.folderPath || '';
      if (!groups[folder]) groups[folder] = [];
      groups[folder].push({ ...f, index: idx });
    });
    return groups;
  }, [files]);

  const totalSize = useMemo(() => files.reduce((sum, item) => sum + item.file.size, 0), [files]);
  const duplicateFilenameSet = useMemo(() => new Set(duplicates.map(d => d.filename)), [duplicates]);
  const bulkUploadSteps = [
    { title: 'Select', description: 'Choose files or a folder' },
    { title: 'Configure', description: 'Confirm files and analysis' },
    { title: 'Upload', description: 'Process documents' },
    { title: 'Review', description: 'Accept results' },
  ];
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 KB';
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  const formatTokenCount = (value) => {
    const number = Number(value || 0);
    if (number >= 1000000) return `${(number / 1000000).toFixed(1)}M`;
    if (number >= 1000) return `${(number / 1000).toFixed(1)}K`;
    return number.toLocaleString();
  };
  const kbTokensAtLimit = kbTokenBucket?.remaining !== null && Number(kbTokenBucket?.remaining ?? 1) <= 0;
  const uploadStageLabel = (item) => {
    if (item.status === 'completed') return 'Draft created';
    if (item.status === 'failed') return 'Failed';
    const stage = UPLOAD_PIPELINE_STAGES.find(s => s.key === (item.stage || item.status));
    return stage?.label || 'Queued';
  };
  const folderFromRelativePath = (relativePath) => {
    if (!relativePath || !relativePath.includes('/')) return 'Current folder';
    return relativePath.split('/').slice(0, -1).join('/');
  };
  const canContinueBulkWizard = wizardStep === 0 ? files.length > 0 : true;
  const displayedWizardStep = uploadResults ? 3 : wizardStep;
  const goToNextBulkStep = () => {
    if (wizardStep === 0 && files.length === 0) return;
    setWizardStep(step => Math.min(step + 1, 1));
  };
  const goToPreviousBulkStep = () => {
    setWizardStep(step => Math.max(step - 1, 0));
  };
  const refreshParentUsage = async () => {
    try {
      await onSuccess?.({ tableOnly: true });
    } catch (err) {
      console.error('Failed to refresh knowledge base usage after upload:', err);
    }
  };

  return (
    <>{ConfirmBulkElement}<Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) return;
        // EIP-570: Escape, the overlay and Radix's X all land here, and the
        // footer Cancel is disabled while uploading, so these were the only
        // ways out of a running import -- and they abandoned it silently.
        if (uploading && !window.confirm(
          'Stop this import? Documents already uploaded have started analysis and will remain.'
        )) {
          return;
        }
        handleClose();
      }}
    >
      <DialogContent className="fixed !top-[5vh] flex max-h-[88vh] w-[calc(100vw-2rem)] max-w-6xl !translate-y-0 flex-col overflow-hidden bg-card p-0 text-card-foreground" onCloseAutoFocus={onCloseAutoFocus}>
        <DialogHeader className="shrink-0 border-b border-border bg-card px-4 py-4 pr-10 text-left sm:px-8">
          <DialogDescription className="sr-only">
            Bulk upload wizard for selecting knowledge base files, reviewing folder structure, and uploading documents.
          </DialogDescription>
          <DialogTitle className="flex items-center gap-3">
            <span className="rounded-lg bg-primary/10 p-2">
              <Upload className="h-5 w-5 text-primary" />
            </span>
            <span>
              <span className="block text-lg font-semibold">Bulk Upload Documents</span>
              <span className="block text-sm font-normal text-muted-foreground">
                Select files or folders, choose whether Eva analyzes them, then review results.
              </span>
            </span>
          </DialogTitle>
          <p className="text-sm text-muted-foreground [overflow-wrap:anywhere]">Destination: {destinationLabel}</p>
        </DialogHeader>

        <div className="shrink-0 border-b border-border bg-muted/30 px-4 py-3 sm:px-10">
          <div className="mx-auto flex max-w-3xl items-center justify-between">
            {bulkUploadSteps.map((step, index) => {
              const stepNum = index + 1;
              const isActive = displayedWizardStep === index;
              const isComplete = displayedWizardStep > index;
              return (
                <div key={step.title} className="flex items-center">
                  <button
                    type="button"
                    aria-current={isActive ? 'step' : undefined}
                    onClick={() => {
                      if (uploadResults) {
                        setWizardStep(3);
                        return;
                      }
                      if (
                        index <= wizardStep ||
                        (index === 1 && files.length > 0) ||
                        (index === 3 && uploadResults)
                      ) {
                        setWizardStep(index);
                      }
                    }}
                    className={cn(
                      'flex min-h-11 flex-col items-center gap-1 rounded-lg px-2 py-1 transition-colors sm:flex-row sm:gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                      isActive ? 'text-primary' : isComplete ? 'text-success' : 'text-muted-foreground'
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium',
                        isActive && 'bg-primary/10 text-primary',
                        isComplete && 'bg-success/10 text-success',
                        !isActive && !isComplete && 'bg-background text-muted-foreground'
                      )}
                    >
                      {isComplete ? <CheckCircle className="h-4 w-4" /> : stepNum}
                    </span>
                    <span className="text-xs font-medium sm:text-sm">{step.title}</span>
                  </button>
                  {index < bulkUploadSteps.length - 1 && (
                    <ChevronRight className="mx-2 hidden h-5 w-5 text-border sm:block" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="min-h-0 overflow-y-auto px-4 py-4 sm:px-10 sm:py-7">
          <input
            ref={fileInputRef}
            type="file"
            accept={allowedFileTypes.accept_string || ".pdf"}
            onChange={handleFilesSelect}
            className="hidden"
            id="bulk-file-upload"
            multiple
            data-testid="bulk-file-upload-input"
          />
          <input
            ref={folderInputRef}
            type="file"
            accept={allowedFileTypes.accept_string || ".pdf"}
            onChange={handleFolderSelect}
            className="hidden"
            id="bulk-folder-upload"
            webkitdirectory=""
            multiple
            data-testid="bulk-folder-upload-input"
          />

          <div className="mx-auto flex w-full max-w-5xl flex-col space-y-6">
          {/* Incomplete/Resumable Uploads Section */}
          {!uploadResults && !uploading && incompleteUploads.length > 0 && !resumingUpload?.awaiting_file && (
            <div className="rounded-xl border border-warning/30 bg-warning/10 p-4 space-y-3">
              <div className="flex items-center gap-2 text-warning font-medium">
                <RefreshCw className="w-4 h-4" />
                <span>Resume Incomplete Uploads</span>
              </div>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {incompleteUploads.slice(0, 5).map((upload) => (
                  <div 
                    key={upload.upload_id}
                    className="flex items-center justify-between bg-background rounded-lg p-2 text-sm"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span className="truncate max-w-[180px]" title={upload.filename}>
                        {upload.filename}
                      </span>
                      <span className="text-xs text-muted-foreground flex-shrink-0">
                        {upload.progress_percent}%
                      </span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleResumeUpload(upload)}
                        className="text-primary hover:text-primary/80 h-7 px-2"
                        data-testid="resume-upload-btn"
                      >
                        <Play className="w-3 h-3 mr-1" />
                        Resume
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCancelIncomplete(upload.upload_id)}
                        className="text-destructive hover:text-destructive/80 h-7 px-2"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Resume File Selection UI */}
          {resumingUpload?.awaiting_file && (
            <div className="rounded-xl border border-primary/30 bg-primary/10 p-4 space-y-3">
              <div className="flex items-center gap-2 text-primary font-medium">
                <RefreshCw className="w-4 h-4" />
                <span>Resuming: {resumingUpload.filename}</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Select the original file to continue uploading. 
                {resumingUpload.missing_chunks?.length} chunk(s) remaining.
              </p>
              <input
                type="file"
                accept={allowedFileTypes.accept_string || ".pdf"}
                onChange={handleResumeFileSelect}
                className="hidden"
                id="resume-file-input"
                data-testid="resume-file-input"
              />
              <div className="flex gap-2">
                <Button
                  onClick={() => document.getElementById('resume-file-input')?.click()}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Select {resumingUpload.filename}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setResumingUpload(null)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {!uploadResults && !resumingUpload?.awaiting_file && wizardStep === 0 && (
            <div className="space-y-4">
              <div
                className="flex min-h-[300px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-border bg-background p-8 text-center transition-colors hover:border-primary"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                data-testid="bulk-upload-dropzone"
              >
                {scanningFiles ? (
                  <>
                    <Loader2 className="mb-3 h-10 w-10 animate-spin text-primary" />
                    <p className="text-lg font-semibold text-foreground">Scanning files...</p>
                    <p className="mt-1 text-sm text-muted-foreground">Counting supported files and reading folder paths.</p>
                  </>
                ) : (
                  <>
                    <CloudUpload className="mb-3 h-12 w-12 text-muted-foreground" />
                    <p className="text-lg font-semibold text-foreground">Drop files or a folder here</p>
                    <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                      Accepted: {(allowedFileTypes.allowed || ['pdf']).map(t => t.toUpperCase()).join(', ')}.
                      Folder uploads preserve nested paths.
                    </p>
                    <div className="mt-5 flex flex-wrap justify-center gap-3">
                      <Button type="button" onClick={() => fileInputRef.current?.click()} className="bg-primary text-primary-foreground hover:bg-primary/90">
                        <FileText className="mr-2 h-4 w-4" />
                        Choose Files
                      </Button>
                      <Button type="button" variant="outline" onClick={() => folderInputRef.current?.click()}>
                        <FolderOpen className="mr-2 h-4 w-4" />
                        Choose Folder
                      </Button>
                    </div>
                    <p className="mt-4 text-xs text-muted-foreground">
                      Eva analyzes supported documents after upload. That analysis uses knowledge base credits.
                    </p>
                    {files.length > 0 && (
                      <div className="mt-5 grid w-full grid-cols-3 gap-3 text-sm">
                        <div className="rounded-xl border border-border bg-card p-3">
                          <p className="text-lg font-semibold text-foreground">{files.length}</p>
                          <p className="text-muted-foreground">Files</p>
                        </div>
                        <div className="rounded-xl border border-border bg-card p-3">
                          <p className="text-lg font-semibold text-foreground">{Object.keys(folderStructure).length}</p>
                          <p className="text-muted-foreground">Folders</p>
                        </div>
                        <div className="rounded-xl border border-border bg-card p-3">
                          <p className="text-lg font-semibold text-foreground">{formatFileSize(totalSize)}</p>
                          <p className="text-muted-foreground">Total</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {!uploadResults && wizardStep === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-xl border border-border bg-background p-4">
                  <p className="text-2xl font-bold text-foreground">{files.length}</p>
                  <p className="text-sm text-muted-foreground">Files selected</p>
                </div>
                <div className="rounded-xl border border-border bg-background p-4">
                  <p className="text-2xl font-bold text-foreground">{Object.keys(folderStructure).length}</p>
                  <p className="text-sm text-muted-foreground">Folders preserved</p>
                </div>
                <div className="rounded-xl border border-border bg-background p-4">
                  <p className="text-2xl font-bold text-foreground">{formatFileSize(totalSize)}</p>
                  <p className="text-sm text-muted-foreground">Total size</p>
                </div>
                <div className="rounded-xl border border-border bg-background p-4">
                  <p className={cn('text-2xl font-bold', duplicates.length ? 'text-warning' : 'text-success')}>{duplicates.length}</p>
                  <p className="text-sm text-muted-foreground">Duplicates</p>
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-background p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    <Sparkles className={cn('mt-0.5 h-5 w-5', kbTokensAtLimit ? 'text-muted-foreground' : 'text-primary')} />
                    <div>
                      <p className="font-semibold text-foreground">Ask Eva to analyze after upload</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Create suggested summaries and classification after the file is saved.
                      </p>
                      {kbTokenBucket && (
                        <p className={cn('mt-1 text-xs', kbTokensAtLimit ? 'text-destructive' : 'text-muted-foreground')}>
                          {kbTokensAtLimit
                            ? 'Knowledge base usage is at limit. Upload drafts without Eva analysis.'
                            : `${formatTokenCount(kbTokenBucket.remaining)} knowledge base credits remaining.`}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-3 rounded-full border border-border bg-card px-4 py-2">
                    <span className={cn('text-sm font-medium', analyzeWithEva ? 'text-primary' : 'text-muted-foreground')}>
                      {analyzeWithEva ? 'On' : 'Off'}
                    </span>
                    <Switch
                      checked={analyzeWithEva}
                      onCheckedChange={setAnalyzeWithEva}
                      disabled={kbTokensAtLimit}
                      aria-label="Ask Eva analysis"
                      data-testid="bulk-analyze-with-eva-checkbox"
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-border bg-background">
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                  <div>
                    <p className="font-semibold text-foreground">Review upload queue</p>
                    <p className="text-sm text-muted-foreground">
                      {uploadMode === 'folder' ? 'Folder paths will be recreated in the knowledge base.' : 'Files will upload into the current folder.'}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Eva analyzes supported documents after upload. That analysis uses knowledge base credits.
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { setFiles([]); setFileQueue([]); setFolderStructure({}); setWizardStep(0); }}>
                    Clear All
                  </Button>
                </div>

                <div className="max-h-80 space-y-2 overflow-y-auto p-4">
                  {Object.entries(groupedFiles).map(([folder, folderFiles]) => (
                    <div key={folder || 'root'} className="space-y-2">
                      {folder && (
                        <div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm text-muted-foreground">
                          <FolderOpen className="h-4 w-4 text-warning" />
                          <span className="min-w-0 font-medium [overflow-wrap:anywhere]">{folder}</span>
                        </div>
                      )}
                      {folderFiles.map(({ file, index, status }) => (
                        <div
                          key={index}
                          className={cn(
                            'flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-3 text-sm',
                            folder && 'ml-4',
                            status === 'cancelled' && 'opacity-50'
                          )}
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            <DocumentTypeBadge filename={file.name} />
                            <div className="min-w-0">
                              <p className="font-medium text-foreground [overflow-wrap:anywhere]" title={file.name}>{file.name}</p>
                              <p className="text-xs text-muted-foreground [overflow-wrap:anywhere]">{formatFileSize(file.size)} {folder ? `in ${folder}` : ''}</p>
                            </div>
                          </div>
                          <div className="flex flex-shrink-0 items-center gap-2">
                            {duplicateFilenameSet.has(file.name) && (
                              <Badge className="border-warning/30 bg-warning/10 text-warning">Duplicate</Badge>
                            )}
                            {!uploading && (
                              <Button variant="ghost" size="sm" onClick={() => removeFile(index)} aria-label={`Remove ${file.name}`} className="h-11 w-11 p-0">
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Upload Progress with Time Estimation */}
          {wizardStep === 2 && uploading && (
            <div className="space-y-4 rounded-2xl border border-border bg-background p-5">
              <div className="flex items-center gap-3">
                {isPaused ? (
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-primary/30 bg-primary/10">
                    <div className="h-2.5 w-2.5 rounded-sm bg-primary" />
                  </div>
                ) : (
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-primary/30 bg-primary/10">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-foreground">
                    {isPaused ? 'Paused' : `Uploading file ${progress.currentFile} of ${progress.totalFiles}`}
                  </p>
                  <p className="text-sm text-muted-foreground [overflow-wrap:anywhere]">
                    {files[progress.currentFile - 1]?.file?.name || 'Processing...'}
                  </p>
                </div>
                <span className="text-lg font-bold text-foreground">{overallProgress}%</span>
              </div>
              
              <div className="h-2 w-full rounded-full bg-muted">
                <div 
                  className="h-2 rounded-full bg-primary transition-all duration-300"
                  style={{ width: `${overallProgress}%` }}
                />
              </div>

              <div className="rounded-xl border border-border bg-background p-3">
                <ul className="divide-y divide-border md:hidden" aria-label="Upload progress">
                  {fileQueue.map((item, idx) => (
                    <li key={`${item.file.name}-${idx}`} className={cn('space-y-3 py-3 first:pt-0 last:pb-0', idx === progress.currentFile - 1 && 'bg-primary/5')}>
                      <div className="flex items-start gap-2">
                        <DocumentTypeBadge filename={item.file.name} />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium [overflow-wrap:anywhere]">{item.relativePath || item.file.name}</p>
                          <p className="text-xs text-muted-foreground">{formatFileSize(item.file.size)}</p>
                        </div>
                      </div>
                      <UploadStatusPill status={item.status} stage={item.stage} analyzeWithEva={analyzeWithEva} />
                      <dl className="space-y-1 text-sm">
                        <div className="flex flex-wrap justify-between gap-x-3"><dt className="text-muted-foreground">Stage</dt><dd>{uploadStageLabel(item)}</dd></div>
                        {analyzeWithEva && <div className="flex flex-wrap justify-between gap-x-3"><dt className="text-muted-foreground">KB usage</dt><dd>{item.status === 'completed' ? (item.kbTokensUsed ?? item.document?.kb_tokens_used) == null ? 'Analyzing…' : formatTokenCount(item.kbTokensUsed ?? item.document?.kb_tokens_used) : '-'}</dd></div>}
                      </dl>
                    </li>
                  ))}
                </ul>
                <table className="hidden w-full table-fixed overflow-hidden rounded-lg text-sm md:table">
                  <colgroup>
                    <col className="w-[46%]" />
                    <col className="w-[28%]" />
                    <col className="w-[14%]" />
                    <col className="w-[12%]" />
                  </colgroup>
                  <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Document</th>
                      <th className="px-3 py-2 text-left font-medium">Status</th>
                      <th className="px-3 py-2 text-left font-medium">Stage</th>
                      <th className="px-3 py-2 text-right font-medium">KB Usage</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {fileQueue.map((item, idx) => {
                      const isCurrent = idx === progress.currentFile - 1;
                      return (
                        <tr key={`${item.file.name}-${idx}`} className={cn(isCurrent && 'bg-primary/5')}>
                          <td className="max-w-0 px-3 py-2">
                            <div className="flex min-w-0 items-center gap-2">
                              <DocumentTypeBadge filename={item.file.name} />
                              <div className="min-w-0">
                                <div className="truncate font-medium text-foreground" title={item.relativePath || item.file.name}>
                                  {item.relativePath || item.file.name}
                                </div>
                                <div className="truncate text-xs text-muted-foreground">{formatFileSize(item.file.size)}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <UploadStatusPill status={item.status} stage={item.stage} analyzeWithEva={analyzeWithEva} />
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">
                            <span className="block min-w-[7.5rem] truncate" title={uploadStageLabel(item)}>
                              {uploadStageLabel(item)}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right font-medium text-foreground">
                            {analyzeWithEva
                              ? item.status === 'completed'
                                ? (item.kbTokensUsed ?? item.document?.kb_tokens_used) == null
                                  ? <span className="text-muted-foreground">Analyzing…</span>
                                  : formatTokenCount(item.kbTokensUsed ?? item.document?.kb_tokens_used)
                                : '-'
                              : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Time Estimation and Speed */}
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {progress.estimatedTimeRemaining 
                    ? formatTimeRemaining(progress.estimatedTimeRemaining)
                    : 'Calculating...'}
                </span>
                <span>{formatSpeed(progress.uploadSpeed)}</span>
              </div>
              <div className="rounded-xl border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
                Uploaded documents are saved as drafts. If selected, Eva analysis runs in this wizard and uses knowledge base credits.
              </div>
            </div>
          )}

          {/* Upload Results */}
          {wizardStep === 3 && uploadResults && (
            <div className="space-y-4">
              {uploadResults.uploaded_count > 0 && (
                <p className="rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm">Drafts saved. Review their metadata in Content, then submit them for approval.</p>
              )}
              <div className="overflow-hidden rounded-2xl border border-border bg-background">
                <div className="border-b border-border px-4 py-3">
                  <p className="font-semibold text-foreground">Upload summary</p>
                  <p className="text-sm text-muted-foreground">
                    {analyzeWithEva ? 'Uploaded drafts may still be processing. Check their status in Content.' : 'Eva metadata analysis was turned off for this upload.'}
                  </p>
                </div>
                <div className="grid gap-3 p-4 sm:grid-cols-3">
                  <div className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-2 text-sm text-muted-foreground">
                        <FileCheck2 className="h-4 w-4" />
                        Uploaded
                      </span>
                      <span className="text-2xl font-semibold text-foreground">{uploadResults.uploaded_count}</span>
                    </div>
                  </div>
                  <div className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-2 text-sm text-muted-foreground">
                        <XCircle className="h-4 w-4 text-destructive" />
                        Failed
                      </span>
                      <span className="text-2xl font-semibold text-destructive">{uploadResults.failed_count}</span>
                    </div>
                  </div>
                  <div className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Sparkles className="h-4 w-4 text-primary" />
                        Eva analysis
                      </span>
                      <span className="text-lg font-semibold text-foreground">{analyzeWithEva ? 'Enabled' : 'Skipped'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {uploadResults.folders_created?.length > 0 && (
                <div className="overflow-hidden rounded-2xl border border-border bg-background">
                  <div className="border-b border-border px-4 py-3">
                    <p className="flex items-center gap-2 font-semibold text-foreground">
                      <Folder className="h-4 w-4 text-primary" />
                      Folders prepared
                    </p>
                  </div>
                  <ul className="divide-y divide-border">{uploadResults.folders_created.map(path =>
                    <li key={path} className="flex items-start gap-2 px-4 py-3 text-sm"><FolderOpen className="mt-0.5 h-4 w-4 shrink-0 text-primary" /><span className="min-w-0 [overflow-wrap:anywhere]">{path}</span></li>
                  )}</ul>
                </div>
              )}

              {(uploadResults.documents?.length > 0 || uploadResults.errors?.length > 0) && (
                <div className="overflow-hidden rounded-2xl border border-border bg-background">
                  <div className="border-b border-border px-4 py-3">
                    <p className="font-semibold text-foreground">Final results</p>
                    <p className="text-sm text-muted-foreground">
                      {uploadResults.uploaded_count ? 'Saved files are drafts. Check processing and metadata before submitting for review.' : 'No documents were saved. Review the errors before trying again.'}
                    </p>
                  </div>
                  <ul className="divide-y divide-border md:hidden" aria-label="Upload results">
                    {uploadResults.documents?.map(doc => <li key={doc.id} className="space-y-2 p-4 text-sm">
                      <p className="font-medium [overflow-wrap:anywhere]">{doc.filename}</p>
                      <p className="text-muted-foreground [overflow-wrap:anywhere]">{folderFromRelativePath(doc.relative_path)}</p>
                      <p className="text-success">Draft saved</p>
                    </li>)}
                    {uploadResults.errors?.map((err,index) => <li key={index} className="space-y-2 p-4 text-sm">
                      <p className="font-medium [overflow-wrap:anywhere]">{err.filename}</p><p className="text-destructive-text [overflow-wrap:anywhere]">{err.error}</p>
                    </li>)}
                  </ul>
                  <table className="hidden w-full text-sm md:table">
                    <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium">Document</th>
                        <th className="px-4 py-3 text-left font-medium">Folder</th>
                        <th className="px-4 py-3 text-left font-medium">Result</th>
                        <th className="px-4 py-3 text-right font-medium">KB Usage</th>
                        <th className="px-4 py-3 text-left font-medium">Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {uploadResults.documents?.map((doc, idx) => (
                        <tr key={doc.id || `${doc.filename}-${idx}`}>
                          <td className="px-4 py-3">
                            <div className="flex min-w-0 items-center gap-2">
                              <DocumentTypeBadge filename={doc.filename} />
                              <div className="min-w-0">
                                <div className="font-medium text-foreground [overflow-wrap:anywhere]" title={doc.relative_path || doc.filename}>
                                  {doc.filename}
                                </div>
                                <div className="truncate text-xs text-muted-foreground">{doc.title}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            <span className="flex items-center gap-2">
                              <Folder className="h-4 w-4 text-muted-foreground" />
                              {folderFromRelativePath(doc.relative_path)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <UploadStatusPill status="completed" stage={analyzeWithEva ? 'complete' : 'draft'} analyzeWithEva={analyzeWithEva} />
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-foreground">
                            {analyzeWithEva
                              ? doc.kb_tokens_used == null
                                ? <span className="text-muted-foreground">Analyzing…</span>
                                : formatTokenCount(doc.kb_tokens_used)
                              : '-'}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">-</td>
                        </tr>
                      ))}
                      {uploadResults.errors?.map((err, idx) => {
                        const uploadError = formatBulkUploadError(err.error);
                        const label = err.label || uploadError.label;
                        const isLimit = ['plan_limit', 'token_limit'].includes(err.type || uploadError.type);
                        return (
                          <tr key={`${err.filename}-${idx}`} className="bg-destructive/5">
                          <td className="px-4 py-3">
                            <div className="flex min-w-0 items-center gap-2">
                                <DocumentTypeBadge filename={err.filename} />
                                <div className="min-w-0">
                                  <div className="truncate font-medium text-foreground" title={err.filename}>{err.filename}</div>
                                  <div className="text-xs text-muted-foreground" title={uploadError.message}>{uploadError.message}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">
                              <span className="flex items-center gap-2">
                                <Folder className="h-4 w-4 text-muted-foreground" />
                                -
                              </span>
                          </td>
                          <td className="px-4 py-3">
                              <UploadStatusPill status="failed" stage="failed" analyzeWithEva={analyzeWithEva} />
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-muted-foreground">-</td>
                          <td className="px-4 py-3">
                              <Badge className={cn(
                                'gap-1',
                                isLimit
                                  ? 'border-destructive/30 bg-destructive/10 text-destructive'
                                  : 'border-warning/30 bg-warning/10 text-warning'
                              )}>
                                <AlertCircle className="h-3 w-3" />
                                {label}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

            </div>
          )}
          </div>
        </div>

        <div className="flex shrink-0 justify-end border-t border-border bg-card px-4 py-3 sm:px-8">
          <div className="flex w-full flex-wrap justify-end gap-2 sm:w-auto sm:items-center">
            <Button
              variant="outline"
              onClick={wizardStep === 0 || wizardStep === 3 ? handleClose : goToPreviousBulkStep}
              disabled={uploading}
              className="min-h-11 min-w-24"
            >
              {wizardStep === 0 || wizardStep === 3 ? 'Cancel' : 'Back'}
            </Button>
            {uploading && (
              <Button
                variant="outline"
                onClick={cancelBulkUpload}
                disabled={cancelling}
                className="min-h-11 min-w-24 border-destructive text-destructive hover:bg-destructive/10"
                data-testid="bulk-stop-btn"
              >
                {cancelling ? 'Stopping...' : 'Stop Import'}
              </Button>
            )}
            {uploading && (
              <Button
                variant="outline"
                onClick={togglePause}
                disabled={cancelling}
                className={cn('min-h-11 min-w-24', isPaused && 'border-primary text-primary')}
              >
                {isPaused ? (
                  <>
                    <Play className="mr-2 h-4 w-4" />
                    Resume
                  </>
                ) : (
                  <>
                    <Pause className="mr-2 h-4 w-4" />
                    Pause
                  </>
                )}
              </Button>
            )}
            {!uploadResults && wizardStep < 1 && (
              <Button
                onClick={goToNextBulkStep}
                disabled={!canContinueBulkWizard || scanningFiles}
                className="flex min-h-11 min-w-28 items-center justify-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Next
              </Button>
            )}
            {!uploadResults && wizardStep === 1 && (
              <Button
                onClick={handleBulkUpload}
                disabled={files.length === 0 || uploading}
                className="flex min-h-11 min-w-28 items-center justify-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="bulk-upload-submit-btn"
              >
                <Upload className="h-4 w-4" />
                Upload
              </Button>
            )}
            {uploading && (
              <Button disabled className="flex min-h-11 min-w-28 items-center justify-center gap-2 bg-primary text-primary-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Uploading
              </Button>
            )}
            {uploadResults && (
              <Button onClick={handleClose} className="min-h-11 min-w-28 bg-primary text-primary-foreground hover:bg-primary/90">
                Done
              </Button>
            )}
          </div>
        </div>

        {/* Duplicate Detection Dialog */}
        {showDuplicateDialog && duplicates.length > 0 && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 rounded-lg">
            <div className="bg-background border rounded-xl p-6 max-w-md w-full shadow-lg space-y-4">
              <div className="flex items-center gap-2 text-warning">
                <AlertCircle className="w-5 h-5" />
                <h3 className="font-semibold">Duplicate Files Detected</h3>
              </div>
              
              <p className="text-sm text-muted-foreground">
                {duplicates.length} file{duplicates.length !== 1 ? 's' : ''} already exist in this knowledge base:
              </p>
              
              <div className="max-h-32 overflow-y-auto space-y-2">
                {duplicates.map((dup, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm bg-secondary rounded p-2">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    <span className="truncate">{dup.filename}</span>
                  </div>
                ))}
              </div>
              
              <p className="text-sm text-muted-foreground">What would you like to do?</p>
              
              <div className="flex flex-col gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => handleDuplicateAction('skip')}
                  className="justify-start"
                >
                  <X className="w-4 h-4 mr-2" />
                  Skip duplicates
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => handleDuplicateAction('copy')}
                  className="justify-start"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Upload another copy
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => handleDuplicateAction('keep_both')}
                  className="justify-start"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Keep both (rename new files)
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog></>
  );
}

// Content Gaps View Component
function ContentGapsView({ gaps, loading, onResolve }) {
  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4">
              <Skeleton className="h-5 w-3/4 mb-2" />
              <Skeleton className="h-4 w-1/2" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!gaps || gaps.length === 0) {
    return (
      <Card className="bg-success/10 border-success/30">
        <CardContent className="p-8 text-center">
          <CheckCircle className="w-12 h-12 text-success mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-success mb-2">No Content Gaps Found</h3>
          <p className="text-success">Great news! Your knowledge base appears to be covering user queries well.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {gaps.map((gap) => (
        <Card key={gap.id} className={`border-l-4 ${gap.status === 'resolved' ? 'border-l-emerald-500 bg-success/10/50' : 'border-l-red-500 bg-card'}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <HelpCircle className={`w-5 h-5 flex-shrink-0 ${gap.status === 'resolved' ? 'text-success' : 'text-destructive'}`} />
                  <h4 className="font-medium text-foreground truncate">{gap.query_pattern}</h4>
                  {gap.status === 'resolved' && (
                    <Badge className="bg-success/10 text-success border-0">
                      <CheckCheck className="w-3 h-3 mr-1" />
                      Resolved
                    </Badge>
                  )}
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm text-muted-foreground mb-3">
                  <div>
                    <span className="text-muted-foreground block text-xs">Query Count</span>
                    <span className="font-medium text-foreground">{gap.query_count}</span>
                  </div>
                  {gap.suggested_topic && (
                    <div>
                      <span className="text-muted-foreground block text-xs">Suggested Topic</span>
                      <Badge variant="outline" className="mt-1">{gap.suggested_topic}</Badge>
                    </div>
                  )}
                  {gap.suggested_region && (
                    <div>
                      <span className="text-muted-foreground block text-xs">Region</span>
                      <span className="font-medium text-foreground">{gap.suggested_region}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted-foreground block text-xs">Last Seen</span>
                    <span className="font-medium text-foreground">
                      {gap.last_seen ? new Date(gap.last_seen).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>

                {/* Eva's Response — what Eva told the user when it flagged this
                    gap, so KB managers can see what to add (EIP-197). Same field
                    the dedicated Content Gaps page surfaces; bounded height so a
                    long answer scrolls inside the card instead of stretching it. */}
                {gap.response && (
                  <div className="mb-3 rounded-xl border border-border bg-muted/40 p-3">
                    <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" />
                      Eva's Response
                    </p>
                    <div className="max-h-40 overflow-y-auto pr-1 text-foreground/90">
                      <MarkdownContent text={gap.response} />
                    </div>
                  </div>
                )}

                {/* Sample Queries */}
                {gap.sample_queries && gap.sample_queries.length > 0 && (
                  <div className="bg-secondary rounded-xl p-3">
                    <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" />
                      Sample Queries
                    </p>
                    <ul className="space-y-1">
                      {gap.sample_queries.slice(0, 3).map((query, idx) => (
                        <li key={idx} className="text-sm text-foreground flex items-start gap-2">
                          <span className="text-muted-foreground">•</span>
                          <span className="italic">&ldquo;{query}&rdquo;</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Actions */}
              {gap.status !== 'resolved' && onResolve && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onResolve(gap.id)}
                  className="flex-shrink-0"
                  data-testid={`resolve-gap-${gap.id}`}
                >
                  <CheckCheck className="w-4 h-4 mr-1" />
                  Mark Resolved
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function restoreUploadFocus(event) {
  const target = document.querySelector('[data-testid="upload-doc-btn"]') || document.querySelector('[data-testid="refresh-table"]');
  if (target) { event.preventDefault(); target.focus(); }
}

export default function KBManagementPage() {
  const { hasPermission, hasRole } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [contentGaps, setContentGaps] = useState([]);
  const [topics, setTopics] = useState([]);
  const [topicsMap, setTopicsMap] = useState({});
  const [billingStatus, setBillingStatus] = useState(null);
  const [kbTokenBucket, setKbTokenBucket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tableRefreshing, setTableRefreshing] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [detailsDoc, setDetailsDoc] = useState(null);
  const detailsReturnFocusRef = useRef(null);
  const detailsReturnIdRef = useRef(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const detailsRequestRef = useRef(0);
  const [docDetails, setDocDetails] = useState(null);
  const [filters, setFilters] = useState({ status: '', topic: '', region: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [activeView, setActiveView] = useState('all'); // 'all', 'top', 'needs-attention', 'unused', 'gaps'
  // Raw, view-agnostic selection. NEVER read this directly — read `selectedDocs`
  // (derived below, next to `filteredDocs`), which is intersected with the
  // documents actually visible under the current filters. (EIP-310)
  const [selectedDocIdsRaw, setSelectedDocs] = useState(new Set());
  const [bulkActionOpen, setBulkActionOpen] = useState(false);
  const [bulkActionType, setBulkActionType] = useState(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [metadataDocuments, setMetadataDocuments] = useState(null);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [_allEmployeesCount, setAllEmployeesCount] = useState(null);
  const [groupFilter, setGroupFilter] = useState(''); // '' = all, GROUP_FILTER_ALL_EMPLOYEES = public, else group id
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [flashedDocId, setFlashedDocId] = useState(null); // For row flash animation after action
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const compactDocuments = useMediaQuery('(max-width: 767px)');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);


  // Folder state
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [folderPanelOpen, setFolderPanelOpen] = useState(false);
  const [moveToFolderOpen, setMoveToFolderOpen] = useState(false);
  const [docsToMove, setDocsToMove] = useState([]);
  const folderTreeRef = useRef(null);
  
  const [ConfirmEl, confirm] = useConfirm();
  
  // Auto-polling for ingestion status
  const [autoPolling, _setAutoPolling] = useState(true);

  // Permission checks
  const canUpload = hasPermission(PERMISSIONS.KB_UPLOAD);
  const canApprove = hasPermission(PERMISSIONS.KB_APPROVE);
  const canPublish = hasPermission(PERMISSIONS.KB_PUBLISH);
  const canDelete = hasPermission(PERMISSIONS.KB_DELETE);
  const canManagePlan = hasRole(['admin']);
  
  // Function to refresh folder counts
  const refreshFolderCounts = () => {
    if (folderTreeRef.current?.refresh) {
      folderTreeRef.current.refresh();
    }
  };
  
  // Auto-polling effect - refresh every 10 seconds if there are documents being ingested
  useEffect(() => {
    if (!autoPolling) return;
    
    // Check if any documents are currently being ingested
    const hasIngesting = documents.some(doc => 
      ['queued', 'parsing', 'chunking', 'embedding'].includes(doc.ingestion_status)
    );
    
    if (hasIngesting) {
      const pollInterval = setInterval(() => {
        fetchDocuments({ tableOnly: true });
        fetchKbTokenBucket();
      }, 10000); // Poll every 10 seconds
      
      return () => clearInterval(pollInterval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPolling, documents]);

  // Fetch topics for referential integrity
  const fetchTopics = async () => {
    try {
      const response = await api.get('/topics');
      const topicsList = response.data || [];
      setTopics(topicsList);
      // Create a lookup map by topic name
      const map = {};
      topicsList.forEach(t => {
        map[t.name] = { color: t.color, icon: t.icon };
      });
      setTopicsMap(map);
    } catch (err) {
      console.error('Failed to fetch topics:', err);
    }
  };

  const fetchDocuments = async ({ tableOnly = false } = {}) => {
    if (tableOnly) {
      setTableRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      if (activeView === 'gaps') {
        // Fetch content gaps
        const response = await api.get('/analytics/content-gaps');
        setContentGaps(response.data);
        setDocuments([]);
      } else if (activeView !== 'all') {
        // Use performance endpoint for smart views
        const params = new URLSearchParams();
        params.append('view', activeView);
        const response = await api.get(`/kb/documents/performance?${params}`);
        setDocuments(prev => tableOnly ? mergeTableRefreshDocuments(prev, response.data) : response.data);
        setContentGaps([]);
      } else {
        // Use regular endpoint for all docs
        const params = new URLSearchParams();
        if (filters.status) params.append('status', filters.status);
        if (filters.topic) params.append('topic', filters.topic);
        if (filters.region) params.append('region', filters.region);
        const response = await api.get(`/kb/documents?${params}`);
        setDocuments(prev => tableOnly ? mergeTableRefreshDocuments(prev, response.data) : response.data);
        setContentGaps([]);
      }
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      toast.error('Error', { description: 'Failed to fetch documents.' });
    } finally {
      if (tableOnly) {
        setTableRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  const refreshCurrentTable = () => fetchDocuments({ tableOnly: true });

  const mergeTableRefreshDocuments = (currentDocs, refreshedDocs = []) => {
    const currentById = new Map(currentDocs.map(doc => [doc.id, doc]));
    return refreshedDocs.map(doc => {
      const currentDoc = currentById.get(doc.id);
      if (!currentDoc) return doc;
      return {
        ...currentDoc,
        ...doc,
        // Keep row order stable without retaining a stale edit revision.
        _sortUpdatedAt: currentDoc._sortUpdatedAt ?? currentDoc.updated_at,
      };
    });
  };

  const fetchBillingStatus = async () => {
    try {
      const response = await api.get('/settings/billing');
      setBillingStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch billing status:', err);
    }
  };

  const fetchKbTokenBucket = async () => {
    try {
      const response = await api.get('/usage');
      setKbTokenBucket(response.data?.token_buckets?.knowledge_base || null);
    } catch (err) {
      console.error('Failed to fetch KB token usage:', err);
      setKbTokenBucket(null);
    }
  };

  const fetchAvailableGroups = async () => {
    try {
      const response = await api.get('/groups');
      setAllEmployeesCount(getAllEmployeesCount(response.data));
      setAvailableGroups(normalizeGroupListResponse(response.data));
    } catch (err) {
      console.error('Failed to fetch groups:', err);
      setAllEmployeesCount(null);
      setAvailableGroups([]);
    }
  };

  const handleUploadSuccess = async () => {
    await Promise.all([
      fetchDocuments({ tableOnly: true }),
      fetchBillingStatus(),
      fetchKbTokenBucket(),
    ]);
    refreshFolderCounts();
  };

  // Fetch topics on mount
  useEffect(() => { fetchTopics(); fetchBillingStatus(); fetchAvailableGroups(); fetchKbTokenBucket(); }, []);
  
  useEffect(() => { fetchDocuments(); }, [filters, activeView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the open details panel aligned with table refreshes and ingestion polling.
  // Workflow and ingestion updates can arrive through the table list first; without
  // this bridge the modal may keep showing an older docDetails snapshot.
  useEffect(() => {
    if (!detailsDoc?.id) return;
    const refreshedRow = documents.find(d => d.id === detailsDoc.id);
    if (!refreshedRow) return;

    setDetailsDoc(prev => prev?.id === refreshedRow.id ? { ...prev, ...refreshedRow } : prev);
    setDocDetails(prev => prev?.id === refreshedRow.id ? { ...prev, ...refreshedRow } : prev);
  }, [documents, detailsDoc?.id]);

  // Helper function for optimistic updates - updates a single document in state
  const updateDocumentInState = (docId, updates, { touchUpdatedAt = false } = {}) => {
    setDocuments(prev => prev.map(d => 
      d.id === docId
        ? { ...d, ...updates, ...(touchUpdatedAt ? { updated_at: new Date().toISOString() } : {}) }
        : d
    ));
    // Also update details if open
    if (detailsDoc?.id === docId) {
      setDocDetails(prev => prev ? { ...prev, ...updates } : prev);
    }
  };

  const refreshDocumentInState = async (docId, { preserveUpdatedAt = false } = {}) => {
    try {
      const response = await api.get(`/kb/documents/${docId}`);
      const freshDoc = response.data;
      setDocuments(prev => prev.map(d => d.id === docId ? {
        ...d,
        ...freshDoc,
        _sortUpdatedAt: preserveUpdatedAt ? (d._sortUpdatedAt ?? d.updated_at) : freshDoc.updated_at,
      } : d));
      if (detailsDoc?.id === docId) {
        setDocDetails(freshDoc);
      }
      return freshDoc;
    } catch (err) {
      console.error('Failed to refresh document:', err);
      return null;
    }
  };

  // Revert optimistic update on error
  const revertDocumentUpdate = (docId, originalDoc) => {
    setDocuments(prev => prev.map(d => d.id === docId ? originalDoc : d));
    if (detailsDoc?.id === docId) {
      setDocDetails(originalDoc);
    }
  };

  const handleAction = async (action, doc, options = {}) => {
    const originalDoc = { ...doc }; // Store original for rollback
    
    try {
      if (action === 'details' || action === 'edit') {
        const request = ++detailsRequestRef.current;
        detailsReturnFocusRef.current = options.returnFocus || document.activeElement;
        detailsReturnIdRef.current = doc.id;
        setDetailsDoc(doc); setDocDetails(null); setDetailsError(''); setDetailsLoading(true);
        try {
          const response = await api.get(`/kb/documents/${doc.id}`);
          if (request === detailsRequestRef.current) setDocDetails(response.data);
        } catch (_err) {
          if (request === detailsRequestRef.current) setDetailsError('Document details could not be loaded. Retry to view or edit metadata.');
        } finally {
          if (request === detailsRequestRef.current) setDetailsLoading(false);
        }
      } else if (action === 'refresh-details') {
        await refreshDocumentInState(doc.id);
      } else if (action === 'metadata-saved') {
        const savedDoc = {
          ...doc,
          ...(options.metadata || {}),
        };
        updateDocumentInState(doc.id, savedDoc);
        setDetailsDoc(prev => prev?.id === doc.id ? { ...prev, ...savedDoc } : prev);
      } else if (action === 'preview') {
        setPreviewDoc(doc);
      } else if (action === 'download') {
        const response = await api.get(`/kb/documents/${doc.id}/download`, {
          responseType: 'blob',
        });
        const disposition = response.headers?.['content-disposition'] || '';
        const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/)?.[1];
        const quotedName = disposition.match(/filename="?([^";]+)"?/)?.[1];
        const filename = encodedName
          ? decodeURIComponent(encodedName)
          : quotedName || doc.original_filename || `${doc.title || 'document'}${doc.file_type || ''}`;
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else if (action === 'suggest') {
        toast.info('Analyzing document...', { description: 'Eva is reading your document.' });
        const response = await api.post(`/kb/documents/${doc.id}/suggest-metadata`);
        if (response.data.suggestions) {
          toast.success('Suggestions Ready', { description: 'Check the document details.' });
        }
      } else if (action === 'move-folder') {
        setDocsToMove([doc]);
        setMoveToFolderOpen(true);
      } else if (action === 'submit') {
        // Optimistic update
        updateDocumentInState(doc.id, { status: 'pending_review' });
        toast.info('Submitted', { description: 'Document sent for review.' });
        try {
          await api.post(`/kb/documents/${doc.id}/submit-for-review`);
          await refreshDocumentInState(doc.id);
        } catch (err) {
          revertDocumentUpdate(doc.id, originalDoc);
          throw err;
        }
      } else if (action === 'approve') {
        // Optimistic update
        updateDocumentInState(doc.id, { status: 'approved', approved_by: 'current_user' });
        toast.success('Approved', { description: 'Document approved.' });
        try {
          await api.post(`/kb/documents/${doc.id}/approve`, { action: 'approve' });
          await refreshDocumentInState(doc.id);
        } catch (err) {
          revertDocumentUpdate(doc.id, originalDoc);
          throw err;
        }
      } else if (action === 'reject') {
        // Optimistic update
        updateDocumentInState(doc.id, { status: 'draft' });
        toast.info('Rejected', { description: 'Document returned to draft.' });
        try {
          await api.post(`/kb/documents/${doc.id}/approve`, { action: 'reject' });
          await refreshDocumentInState(doc.id);
        } catch (err) {
          revertDocumentUpdate(doc.id, originalDoc);
          throw err;
        }
      } else if (action === 'publish') {
        // Optimistic update. Publish no longer touches ingestion_status
        // server-side (EIP-497), so don't fake 'queued' here either.
        updateDocumentInState(doc.id, { status: 'published' });
        toast.success('Published', { description: 'Document is being published...' });
        try {
          await api.post(`/kb/documents/${doc.id}/publish`);
          await refreshDocumentInState(doc.id);
          // Start SSE for real-time updates
          startIngestionStream(doc.id);
          // The live flip happens in a server background task AFTER the
          // publish response, and for an already-'complete' doc the SSE
          // stream terminates on its first poll - both refetches above can
          // win that race and leave the row showing published-not-live.
          // One delayed refetch observes the settled state.
          setTimeout(() => refreshDocumentInState(doc.id), 2500);
        } catch (err) {
          revertDocumentUpdate(doc.id, originalDoc);
          throw err;
        }
      } else if (action === 'archive') {
        const ok = await confirm({
          title: 'Archive Document',
          description: 'This will remove the document from active workflow and search availability. You can restore it to draft later.',
          confirmLabel: 'Archive',
          variant: 'warning',
        });
        if (!ok) return;

        // Optimistic update
        updateDocumentInState(doc.id, { status: 'archived' });
        toast.info('Archived', { description: 'Document has been archived.' });
        try {
          await api.post(`/kb/documents/${doc.id}/archive`);
          await Promise.all([refreshDocumentInState(doc.id), fetchBillingStatus()]);
        } catch (err) {
          revertDocumentUpdate(doc.id, originalDoc);
          throw err;
        }
      } else if (action === 'restore') {
        // Optimistic update
        updateDocumentInState(doc.id, { status: 'draft' });
        toast.success('Restored', { description: 'Document restored to draft.' });
        try {
          await api.post(`/kb/documents/${doc.id}/restore`);
          await Promise.all([refreshDocumentInState(doc.id), fetchBillingStatus()]);
        } catch (err) {
          revertDocumentUpdate(doc.id, originalDoc);
          throw err;
        }
      } else if (action === 'retry-ingestion') {
        // Optimistic update
        updateDocumentInState(doc.id, { ingestion_status: 'queued', ingestion_error: null });
        toast.info('Retrying', { description: 'Document ingestion has been requeued.' });
        try {
          await api.post(`/kb/documents/${doc.id}/retry-ingestion`);
          await refreshDocumentInState(doc.id);
          startIngestionStream(doc.id);
        } catch (err) {
          revertDocumentUpdate(doc.id, originalDoc);
          throw err;
        }
      } else if (action === 'make-live') {
        // Optimistic update - MUST update both status and visibility
        updateDocumentInState(doc.id, { status: 'live', visibility: 'all_employees' });
        toast.success('Live!', { description: 'Document is now available to all employees.' });
        try {
          await api.post(`/kb/documents/${doc.id}/make-live`);
          await refreshDocumentInState(doc.id);
        } catch (err) {
          revertDocumentUpdate(doc.id, originalDoc);
          throw err;
        }
      } else if (action === 'delete') {
        const ok = await confirm({ title: 'Delete Document', description: 'Are you sure you want to delete this document?', confirmLabel: 'Delete', variant: 'delete' });
        if (ok) {
          // Optimistic update - remove from list
          setDocuments(prev => prev.filter(d => d.id !== doc.id));
          toast.success('Deleted', { description: 'Document has been removed.' });
          try {
            await api.delete(`/kb/documents/${doc.id}`);
            // Refresh capacity after successful deletion.
            await fetchBillingStatus();
            refreshFolderCounts();
          } catch (err) {
            // Revert - add back to list
            setDocuments(prev => [...prev, originalDoc]);
            throw err;
          }
        }
        return; // Don't flash deleted rows
      }
      
      if (!options.keepDetailsOpen && ['submit', 'approve', 'reject', 'publish', 'archive', 'restore', 'retry-ingestion', 'make-live'].includes(action)) {
        setDetailsDoc(null);
        setDocDetails(null);
      }

      // Flash the row to indicate update (for non-delete actions)
      flashRow(doc.id);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        showLimitError(detail, null, 'Action failed', { canUseCta: canManagePlan });
      } else {
        toast.error('Action Failed', { description: detail || err.message });
      }
    }
  };
  
  // Flash a row briefly to indicate it was updated
  const flashRow = (docId) => {
    setFlashedDocId(docId);
    setTimeout(() => setFlashedDocId(null), 2000);
  };

  const handleResolveGap = async (gapId) => {
    try {
      await api.post(`/analytics/content-gaps/${gapId}/resolve`);
      toast.success('Gap Resolved', { description: 'Content gap marked as resolved.' });
      fetchDocuments();
    } catch (err) {
      toast.error('Action Failed', { description: err.response?.data?.detail || err.message });
    }
  };

  // SSE for real-time ingestion status updates
  const startIngestionStream = async (docId) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const token = localStorage.getItem('eva_token');

    const pollDocumentUntilTerminal = async () => {
      for (let i = 0; i < 120; i += 1) {
        const freshDoc = await refreshDocumentInState(docId, { preserveUpdatedAt: true });
        const status = freshDoc?.ingestion_status;
        if (['complete', 'indexed'].includes(status)) {
          toast.success('Ingestion Complete', { description: `Document indexed with ${freshDoc.chunk_count || 'N/A'} pages.` });
          await fetchDocuments({ tableOnly: true });
          return;
        }
        if (status === 'failed') {
          toast.error('Ingestion Failed', { description: freshDoc.ingestion_error || 'Unknown error' });
          await fetchDocuments({ tableOnly: true });
          return;
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      await fetchDocuments({ tableOnly: true });
    };

    if (!token) {
      console.warn('No auth token available for ingestion stream; falling back to polling.');
      pollDocumentUntilTerminal();
      return;
    }
    
    try {
      // Use fetch for SSE since axios doesn't support streaming well
      const response = await fetch(`${API_URL}/api/kb/documents/${docId}/ingestion-stream`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
        }
      });
      
      if (!response.ok) {
        console.error('SSE connection failed:', response.status);
        pollDocumentUntilTerminal();
        return;
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        
        const text = decoder.decode(value);
        const lines = text.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.done) {
                // Stream completed - refresh data
                fetchDocuments({ tableOnly: true });
                // Always update details if this doc is open (check via functional update)
                setDocDetails(prev => {
                  if (prev?.id === docId) {
                    // Fetch fresh data
                    api.get(`/kb/documents/${docId}`).then(res => setDocDetails(res.data));
                  }
                  return prev;
                });
                break;
              }
              
              if (data.error) {
                toast.error('Ingestion Error', { description: data.error });
                break;
              }
              
              // Update document row in table in real-time
              updateDocumentInState(docId, {
                ingestion_status: data.status,
                ingestion_error: data.error,
                chunk_count: data.chunk_count,
                visibility: data.visibility
              });
              
              // Update docDetails with new status in real-time (use functional update to avoid stale closure)
              setDocDetails(prev => {
                if (prev?.id === docId) {
                  return {
                    ...prev,
                    ingestion_status: data.status,
                    ingestion_error: data.error,
                    chunk_count: data.chunk_count,
                    visibility: data.visibility
                  };
                }
                return prev;
              });
              
              // Show toast for final statuses (without toast spam)
              if (['complete', 'indexed'].includes(data.status)) {
                toast.success('Ingestion Complete', { description: `Document indexed with ${data.chunk_count || 'N/A'} pages.` });
                // Refresh full list to get any additional changes
                fetchDocuments({ tableOnly: true });
              } else if (data.status === 'failed') {
                toast.error('Ingestion Failed', { description: data.error || 'Unknown error' });
                fetchDocuments({ tableOnly: true });
              }
            } catch (e) {
              console.error('Failed to parse SSE data:', e);
            }
          }
        }
      }
    } catch (err) {
      console.error('SSE error:', err);
      pollDocumentUntilTerminal();
    }
  };

  // Bulk metadata cascading dropdowns


  // Selection handlers
  const handleSelectDoc = (docId) => {
    setSelectedDocs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return newSet;
    });
  };

  // Selects every document in the current filtered view (the whole folder /
  // search result), not just the visible page — "select all" should mean all.
  // Identity-based, so a partial selection expands to "all" instead of
  // collapsing to empty. Safe because `selectedDocs` is view-scoped. (EIP-310)
  const handleSelectAll = () => {
    const allVisibleSelected =
      allFilteredDocs.length > 0 && allFilteredDocs.every(d => selectedDocs.has(d.id));
    setSelectedDocs(allVisibleSelected ? new Set() : new Set(allFilteredDocs.map(d => d.id)));
  };

  const clearSelection = () => setSelectedDocs(new Set());

  const updateSort = (key) => {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortOrder(['title', 'topic', 'status', 'region', 'ingestion_status', 'groups'].includes(key) ? 'asc' : 'desc');
    }
  };

  // Get selected documents with their status info for filtering bulk actions
  const getSelectedDocsWithStatus = () => {
    return documents.filter(d => selectedDocs.has(d.id));
  };

  // Check if bulk action is valid for all selected documents
  const isBulkActionValidForSelection = (action) => {
    const selected = getSelectedDocsWithStatus();
    if (selected.length === 0) return false;

    switch (action) {
      case 'submit_for_review':
        return selected.every(d => d.status === 'draft');
      case 'approve':
      case 'reject':
        return selected.every(d => d.status === 'pending_review');
      case 'publish':
        return selected.every(d => d.status === 'approved');
      case 'archive':
        return selected.every(d => ['published', 'live', 'approved', 'draft'].includes(d.status));
      case 'delete':
        return selected.every(d => d.status !== 'published');
      case 'metadata':
        return true; // Metadata can be edited for any status (draft preferred)
      default:
        return true;
    }
  };

  // Get count of invalid docs for a bulk action
  const getInvalidDocsCount = (action) => {
    const selected = getSelectedDocsWithStatus();
    
    switch (action) {
      case 'submit_for_review':
        return selected.filter(d => d.status !== 'draft').length;
      case 'approve':
      case 'reject':
        return selected.filter(d => d.status !== 'pending_review').length;
      case 'publish':
        return selected.filter(d => d.status !== 'approved').length;
      case 'archive':
        return selected.filter(d => !['published', 'live', 'approved', 'draft'].includes(d.status)).length;
      case 'delete':
        return selected.filter(d => d.status === 'published' || d.status === 'live').length;
      default:
        return 0;
    }
  };

  // Bulk action handlers
  const handleBulkAction = async () => {
    if (selectedDocs.size === 0) return;
    
    setBulkActionLoading(true);
    const docIds = Array.from(selectedDocs);
    
    try {
      let response;
      
      if (bulkActionType === 'delete') {
        response = await api.post('/kb/documents/bulk/delete', { document_ids: docIds });
        toast.success('Bulk Delete Complete', { description: `${response.data.success_count} documents deleted.` });
        // Refresh folder counts after bulk delete
        refreshFolderCounts();
      } else if (['submit_for_review', 'approve', 'reject', 'publish', 'archive'].includes(bulkActionType)) {
        response = await api.post('/kb/documents/bulk/status', { 
          document_ids: docIds, 
          action: bulkActionType 
        });
        toast.success('Bulk Status Update', { description: `${response.data.success_count} documents updated.` });
      }
      
      clearSelection();
      setBulkActionOpen(false);
      setBulkActionType(null);
      await Promise.all([fetchDocuments({ tableOnly: true }), fetchBillingStatus()]);
      
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        showLimitError(detail, null, 'Bulk action failed', { canUseCta: canManagePlan });
      } else {
        toast.error('Bulk Action Failed', { description: detail || err.message });
      }
    } finally {
      setBulkActionLoading(false);
    }
  };

  const openBulkAction = (action) => {
    if (action === 'metadata') {
      setMetadataDocuments(getSelectedDocsWithStatus().map(d => ({ ...d })));
    } else if (action === 'move-folder') {
      // Handle bulk move to folder separately
      const selectedDocsList = documents.filter(d => selectedDocs.has(d.id));
      setDocsToMove(selectedDocsList);
      setMoveToFolderOpen(true);
    } else {
      setBulkActionType(action);
      setBulkActionOpen(true);
    }
  };

  // Filtering and sorting
  const allFilteredDocs = documents
    .filter((doc) => {
      // Search filter
      if (searchQuery && !doc.title.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      // Folder filter
      if (selectedFolderId === 'unfiled') {
        // Show only documents without a folder
        if (doc.folder_id) return false;
      } else if (selectedFolderId && selectedFolderId !== null) {
        // Show only documents in the selected folder
        if (doc.folder_id !== selectedFolderId) return false;
      }
      // Group filter (client-side; literal column value)
      if (!docMatchesGroupFilter(doc, groupFilter, availableGroups)) return false;
      // Topic filter. Redundant in the 'all' view (the backend already applied
      // it), but the Smart Views fetch from /kb/documents/performance, which
      // accepts only `view` — so without this the dropdown is inert there for
      // every topic, not just Uncategorized. Safe because that endpoint does
      // SELECT d.*, so `topic` is present on the rows. (EIP-312)
      if (!docMatchesTopicFilter(doc, filters.topic)) return false;
      // If selectedFolderId is null, show all documents
      return true;
    })
    .sort((a, b) => {
      let aVal = sortBy === 'groups' ? groupSortKey(a, availableGroups) : sortBy === 'retrieval_count' ? (a.retrievals ?? a.retrieval_count) : sortBy === 'updated_at' ? (a._sortUpdatedAt ?? a.updated_at) : a[sortBy];
      let bVal = sortBy === 'groups' ? groupSortKey(b, availableGroups) : sortBy === 'retrieval_count' ? (b.retrievals ?? b.retrieval_count) : sortBy === 'updated_at' ? (b._sortUpdatedAt ?? b.updated_at) : b[sortBy];
      
      // Handle string comparison
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const result = sortOrder === 'asc' 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
        // Use ID as tiebreaker for stable sort (prevents row jumping)
        return result !== 0 ? result : a.id.localeCompare(b.id);
      }
      
      // Handle numeric/date comparison
      let result;
      if (sortOrder === 'asc') {
        result = (aVal || '') > (bVal || '') ? 1 : (aVal || '') < (bVal || '') ? -1 : 0;
      } else {
        result = (aVal || '') < (bVal || '') ? 1 : (aVal || '') > (bVal || '') ? -1 : 0;
      }
      // Use ID as tiebreaker for stable sort (prevents row jumping)
      return result !== 0 ? result : a.id.localeCompare(b.id);
    });

  // Pagination
  const totalPages = Math.max(Math.ceil(allFilteredDocs.length / pageSize), 1);
  const filteredDocs = allFilteredDocs.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // EIP-310: a selection is only actionable if the document is in the view the
  // user is currently looking at. Derived from `allFilteredDocs` — the same
  // expression that feeds the table — so any filter added to that pipeline
  // narrows the selection for free. Every consumer below (count badge, dialog
  // copy, status validation, request payload, move-to-folder) reads this, so
  // they are provably consistent. Not the paginated slice: documents on other
  // pages of the same folder are legitimately in view.
  const visibleDocIds = new Set(allFilteredDocs.map(d => d.id));
  const selectedDocs = new Set(
    Array.from(selectedDocIdsRaw).filter(id => visibleDocIds.has(id))
  );
  const allFilteredSelected =
    allFilteredDocs.length > 0 && allFilteredDocs.every(d => selectedDocs.has(d.id));
  const someFilteredSelected = !allFilteredSelected && selectedDocs.size > 0;

  const [folderPath, setFolderPath] = useState([]);
  const [folderRevision, setFolderRevision] = useState(0);
  useEffect(() => {
    let active = true;
    setFolderPath([]);
    if (selectedFolderId && selectedFolderId !== 'unfiled') {
      api.get('/kb/folders/flat').then(({ data }) => {
        if (!active) return;
        const byId = new Map((data || []).map(f => [f.id, f]));
        const path = [];
        const seen = new Set();
        let folder = byId.get(selectedFolderId);
        while (folder && !seen.has(folder.id)) {
          seen.add(folder.id);
          path.unshift(folder);
          folder = byId.get(folder.parent_id);
        }
        setFolderPath(path);
      }).catch(() => { /* Keep the explicit folder filter visible even if its name could not load. */ });
    }
    return () => { active = false; };
  }, [selectedFolderId, folderRevision]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filters, groupFilter, activeView, selectedFolderId, sortBy, sortOrder, pageSize]);

  // Drop the selection when the user moves to a different set of documents, so a
  // selection never resurrects on navigating back. Deliberately excludes
  // currentPage/pageSize/sortBy/sortOrder: those re-slice or re-order the *same*
  // set, and a folder-wide select-all must survive paging through it.
  // Correctness does not depend on this list being complete — the intersection
  // that derives `selectedDocs` is the guarantee; this is for predictable UX.
  // (EIP-310)
  useEffect(() => {
    setSelectedDocs(new Set());
  }, [searchQuery, filters, groupFilter, activeView, selectedFolderId]);

  const stats = {
    total: documents.length,
    draft: documents.filter((d) => d.status === 'draft').length,
    pending: documents.filter((d) => d.status === 'pending_review').length,
    published: documents.filter((d) => d.status === 'published' || d.status === 'live').length,
    live: documents.filter((d) => ['published', 'live'].includes(d.status) && ['indexed', 'complete'].includes(d.ingestion_status)).length,
    ingestionFailed: documents.filter((d) => d.ingestion_status === 'failed').length,
    // Uploaded but not yet searchable in Eva: anything that isn't live/indexed and
    // isn't archived (mirrors the `isLive` availability gate used elsewhere). (EIP-196)
    notReady: documents.filter(
      (d) =>
        d.status !== 'archived' &&
        !(d.status === 'live' || (d.status === 'published' && ['indexed', 'complete'].includes(d.ingestion_status)))
    ).length,
  };
  // Billing uses the same tenant-wide count as upload enforcement. Table totals
  // include archived rows and can also be narrowed by filters.
  const documentLimitUsed = billingStatus?.usage?.documents ?? documents.filter(d => d.status !== 'archived').length;

  // Smart view tabs config
  const smartViews = [
    { id: 'all', label: 'All Documents', icon: FileText },
    { id: 'top', label: 'Top Performers', icon: TrendingUp, color: 'text-success' },
    { id: 'needs-attention', label: 'Needs Attention', icon: AlertCircle, color: 'text-warning' },
    { id: 'unused', label: 'Unused', icon: Clock, color: 'text-muted-foreground' },
    { id: 'gaps', label: 'Content Gaps', icon: Search, color: 'text-destructive' },
  ];
  
  // Determine if we should show performance columns (for performance smart views)
  const showPerformance = ['top', 'needs-attention', 'unused'].includes(activeView);

  const activeFilterCount = [filters.topic, filters.status, groupFilter].filter(Boolean).length;
  const documentFilters = (<>
              <Select value={filters.topic} onValueChange={(v) => setFilters({ ...filters, topic: v === 'all' ? '' : v })}>
                <SelectTrigger className="h-11 w-full md:h-9 md:w-36" aria-label="Filter by topic" data-testid="topic-filter"><SelectValue placeholder="Topic" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Topics</SelectItem>
                  {/* Unconditional: deriving it from `documents` would be
                      self-erasing, since that list is already topic-filtered —
                      picking any real topic would remove this option. (EIP-312) */}
                  <SelectItem value={TOPIC_FILTER_UNCATEGORIZED}>{UNCATEGORIZED_TOPIC_LABEL}</SelectItem>
                  {topics
                    .filter(t => t.status !== 'deprecated')
                    // A tenant may have a real topic named "Uncategorized";
                    // drop it so the dropdown has no duplicate label. The
                    // sentinel option is a strict superset of what it matched.
                    .filter(t => (t.name || '').trim().toLowerCase() !== UNCATEGORIZED_TOPIC_LABEL.toLowerCase())
                    .map((t) => (<SelectItem key={t.id || t.name} value={t.name}>{t.name}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
                <SelectTrigger className="h-11 w-full md:h-9 md:w-32" aria-label="Filter by status" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="pending_review">Pending Review</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
              <Select value={groupFilter || 'all'} onValueChange={(v) => setGroupFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="h-11 w-full md:h-9 md:w-40" aria-label="Filter by group" data-testid="group-filter"><SelectValue placeholder="Group" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Groups</SelectItem>
                  <SelectItem value={GROUP_FILTER_ALL_EMPLOYEES}>All Employees</SelectItem>
                  {availableGroups.map((g) => (<SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>))}
                </SelectContent>
              </Select>
  </>);
  const folderControls = (<>
              <Button
                variant="outline"
                size="icon"
                className={compactDocuments ? 'h-11 w-auto shrink-0 gap-2 px-3' : 'h-11 w-11 shrink-0'}
                onClick={() => setFolderPanelOpen(true)}
                data-testid="open-folders-btn"
                aria-label="Open folders"
                title="Folders"
              >
                <FolderOpen className="h-4 w-4" />
                {compactDocuments && 'Folders'}
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-11 w-11 shrink-0"
                onClick={refreshCurrentTable}
                disabled={loading || tableRefreshing}
                data-testid="refresh-table"
                aria-label="Refresh documents"
              >
                <RefreshCw className={`w-4 h-4 ${tableRefreshing ? 'animate-spin' : ''}`} />
              </Button>
  </>);
  const visibleFolderPath = selectedFolderId === 'unfiled' ? [{ id: 'unfiled', name: 'Unfiled' }] : folderPath.length ? folderPath : [{ id: selectedFolderId, name: 'Selected folder' }];
  const documentRows = filteredDocs.map(doc => (
    <DocumentRow key={doc.id} doc={doc} onAction={handleAction} canUpload={canUpload} canApprove={canApprove} canPublish={canPublish} canDelete={canDelete} showPerformance={showPerformance} isSelected={selectedDocs.has(doc.id)} onSelect={handleSelectDoc} topicsMap={topicsMap} isFlashing={flashedDocId === doc.id} availableGroups={availableGroups} compact={compactDocuments} />
  ));
  
  return (
    <div className="mx-auto w-full max-w-7xl space-y-6 px-0 py-6 sm:px-6 lg:px-8 animate-fade-in" data-testid="kb-management-page">
      {ConfirmEl}
      {/* Page Banner */}
      <PageBanner pageKey="kb-content" />
      
      {/* Header */}
      <PageHeader
        title="Content"
        description="Upload documents, track performance, and identify content gaps"
        actions={(
          <div className="flex flex-wrap items-center justify-end gap-2">
            {canUpload && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button data-testid="upload-doc-btn" aria-label="Upload" className="min-h-11">
                    <Upload className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline">Upload</span>
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setUploadOpen(true)}>
                    <FileText className="w-4 h-4 mr-2" />
                    Single Document
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setBulkUploadOpen(true)} data-testid="bulk-upload-btn">
                    <Upload className="w-4 h-4 mr-2" />
                    Bulk Upload
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        )}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          title="Total Documents"
          value={stats.total.toLocaleString()}
          subtitle={`${stats.published.toLocaleString()} published`}
          icon={FileText}
          color="primary"
        />
        <KpiCard
          title="Drafts"
          value={stats.draft.toLocaleString()}
          subtitle="Not yet submitted"
          icon={Edit}
          color="blue"
        />
        <KpiCard
          title="Review Queue"
          value={stats.pending.toLocaleString()}
          subtitle="Awaiting approval"
          icon={Clock}
          color="warning"
        />
        <KpiCard
          title="Live"
          value={stats.live.toLocaleString()}
          subtitle={stats.ingestionFailed > 0 ? `${stats.ingestionFailed} ingestion failed` : 'Available to Eva'}
          icon={Database}
          color={stats.ingestionFailed > 0 ? 'destructive' : 'success'}
        />
      </div>

      {stats.notReady > 0 && (
        <KbNotIngestedNotice count={stats.notReady} data-testid="kb-not-ingested-notice" />
      )}

      {billingStatus?.plan_limits && (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <LimitMeter
            title="Documents"
            used={documentLimitUsed}
            limit={billingStatus.plan_limits.document_limit}
            icon={FolderOpen}
            displayType="capacity"
            caption="Knowledge base capacity"
            explanation="Published, draft, and processing documents count toward document capacity. Archiving a document frees its slot."
            showManagePlanCta
            testId="document-limit-meter"
          />
          {kbTokenBucket && (
            <LimitMeter
              title="Eva Insights Credits"
              used={kbTokenBucket.used || 0}
              limit={kbTokenBucket.limit}
              percentUsed={kbTokenBucket.percent_used}
              icon={Sparkles}
              formatter={tokenLimitFormatter}
              caption="Shared by content team workflows"
              explanation="Used by upload analysis, Ask Eva metadata extraction, and content preparation."
              showManagePlanCta
              testId="kb-token-limit-meter"
            />
          )}
          <PlanRulesCard maxPagesPerDocument={billingStatus.plan_limits.max_chunks_per_document} />
        </div>
      )}

      {/* Main Content */}
      <div className="space-y-6">
          {/* Smart View Tabs */}
          {compactDocuments ? (
            <Select value={activeView} onValueChange={setActiveView}>
              <SelectTrigger className="h-11 w-full bg-card" aria-label="Document view"><SelectValue /></SelectTrigger>
              <SelectContent>{smartViews.map(view => <SelectItem key={view.id} value={view.id}>{view.label}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
          <div className="overflow-x-auto rounded-2xl border border-border bg-card/80 p-3 shadow-sm">
            <div className="grid min-w-[760px] grid-cols-5 rounded-xl bg-secondary p-1.5 sm:min-w-0">
              {smartViews.map((view) => {
                const Icon = view.icon;
                const isActive = activeView === view.id;
                return (
                  <button
                    key={view.id}
                    type="button"
                    onClick={() => setActiveView(view.id)}
                    className={cn(
                      'inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      isActive
                        ? 'bg-background text-foreground shadow'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                    data-testid={`view-${view.id}`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="whitespace-nowrap">{view.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          )}

      {/* Smart View Info Banner */}
      {activeView !== 'all' && (
        <Card className={`border-l-4 ${
          activeView === 'top' ? 'border-l-emerald-500 bg-success/10 ' :
          activeView === 'needs-attention' ? 'border-l-amber-500 bg-warning/10 ' :
          activeView === 'gaps' ? 'border-l-red-500 bg-destructive/10 ' :
          'border-l-slate-400 bg-secondary dark:bg-card/50'
        }`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              {activeView === 'top' && (
                <>
                  <TrendingUp className="w-5 h-5 text-success" />
                  <div>
                    <p className="font-medium text-success ">Top Performers</p>
                    <p className="text-sm text-success dark:text-success">Documents with high retrievals (10+) and excellent feedback (80%+ helpful rate)</p>
                  </div>
                </>
              )}
              {activeView === 'needs-attention' && (
                <>
                  <AlertCircle className="w-5 h-5 text-warning" />
                  <div>
                    <p className="font-medium text-warning ">Needs Attention</p>
                    <p className="text-sm text-warning dark:text-warning">Documents with high traffic but low feedback scores - review and improve content</p>
                  </div>
                </>
              )}
              {activeView === 'unused' && (
                <>
                  <Clock className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-foreground dark:text-muted-foreground">Unused Documents</p>
                    <p className="text-sm text-muted-foreground dark:text-muted-foreground">Published documents with zero retrievals in the last 30 days - consider archiving or improving</p>
                  </div>
                </>
              )}
              {activeView === 'gaps' && (
                <>
                  <HelpCircle className="w-5 h-5 text-destructive" />
                  <div>
                    <p className="font-medium text-destructive ">Content Gaps</p>
                    <p className="text-sm text-destructive dark:text-destructive">User queries that returned no relevant documents - create content to fill these gaps</p>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk Action Bar - Shows when documents are selected */}
      {selectedDocs.size > 0 && activeView !== 'gaps' && (
        <Card className="bg-secondary dark:bg-card/50 border-border dark:border-border">
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Badge className="bg-primary/10 text-primary   border-0 px-3 py-1">
                  {selectedDocs.size} selected
                </Badge>
                <Button variant="ghost" size="sm" onClick={clearSelection} className="text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4 mr-1" /> Clear
                </Button>
              </div>
              {/* Actions ordered by workflow: Draft → Review → Approve → Publish → Other */}
              <div className="flex flex-wrap gap-2">
                {/* Workflow actions */}
                {canUpload && (
                  <Button variant="outline" size="sm" onClick={() => openBulkAction('submit_for_review')} data-testid="bulk-submit">
                    <Send className="w-4 h-4 mr-1 text-info" /> Submit for Review
                  </Button>
                )}
                {canApprove && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => openBulkAction('approve')} data-testid="bulk-approve">
                      <CheckCircle className="w-4 h-4 mr-1 text-success" /> Approve
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openBulkAction('reject')} data-testid="bulk-reject">
                      <XCircle className="w-4 h-4 mr-1 text-warning" /> Reject
                    </Button>
                  </>
                )}
                {canPublish && (
                  <Button variant="outline" size="sm" onClick={() => openBulkAction('publish')} data-testid="bulk-publish">
                    <Upload className="w-4 h-4 mr-1 text-info" /> Start Ingestion
                  </Button>
                )}
                {/* Metadata & organization */}
                {canUpload && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => openBulkAction('metadata')} data-testid="bulk-metadata">
                      <Edit className="w-4 h-4 mr-1 text-muted-foreground" /> Edit Metadata
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openBulkAction('move-folder')} data-testid="bulk-move-folder">
                      <FolderInput className="w-4 h-4 mr-1 text-muted-foreground" /> Move to Folder
                    </Button>
                  </>
                )}
                {/* Archive & Delete */}
                {canDelete && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => openBulkAction('archive')} data-testid="bulk-archive">
                      <Archive className="w-4 h-4 mr-1 text-muted-foreground" /> Archive
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openBulkAction('delete')} data-testid="bulk-delete">
                      <Trash2 className="w-4 h-4 mr-1 text-destructive" /> Delete
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {selectedFolderId && (
        <div className="mb-4 space-y-1" data-testid="folder-filter-pill">
          <div className="flex items-start gap-2">
            <nav aria-label="Folder breadcrumb" className="min-w-0 flex-1 text-sm">
              <ol className="flex flex-wrap items-center gap-x-1">
                <li><Button variant="ghost" className="min-h-11 px-2 text-xs md:text-sm" onClick={() => setSelectedFolderId(null)}>All documents</Button></li>
                {visibleFolderPath.map(folder => (
                  <li key={folder.id} className={cn('flex min-w-0 max-w-full items-center gap-1', compactDocuments && folder.id === selectedFolderId && 'w-full')}>
                    {(!compactDocuments || folder.id !== selectedFolderId) && <ChevronRight aria-hidden="true" className="h-3 w-3 shrink-0 text-muted-foreground" />}
                    {compactDocuments && folder.id === selectedFolderId ? <span aria-current="location" className="px-2 py-1 text-lg font-semibold [overflow-wrap:anywhere]">{folder.name}</span> : <Button variant="ghost" className="h-auto min-h-11 max-w-full whitespace-normal px-2 text-left text-xs md:text-sm [overflow-wrap:anywhere]" aria-current={folder.id === selectedFolderId ? 'location' : undefined} onClick={() => setSelectedFolderId(folder.id)}>{folder.name}</Button>}
                  </li>
                ))}
              </ol>
            </nav>
            <Button variant="ghost" className="h-11 w-11 shrink-0 p-0" onClick={() => setSelectedFolderId(null)} aria-label="Clear folder filter"><X className="w-4 h-4" /></Button>
          </div>
          <p className="px-2 text-sm text-muted-foreground">{allFilteredDocs.length} document{allFilteredDocs.length !== 1 ? 's' : ''} in this folder</p>
        </div>
      )}

      {/* Content Gaps View OR Documents Table */}
      {activeView === 'gaps' ? (
        <ContentGapsView gaps={contentGaps} loading={loading} onResolve={canApprove ? handleResolveGap : null} />
      ) : (
        <Card className="bg-card border-border">
          <div className="border-b border-border p-3 sm:p-4">
            {compactDocuments ? (
              <div className="space-y-3">
                <div className="relative">
                  <Search aria-hidden="true" className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input className="h-11 pl-9" value={searchQuery} onChange={event => setSearchQuery(event.target.value)} placeholder="Search documents..." aria-label="Search documents" data-testid="search-input" />
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" className="h-11 flex-1 gap-2" onClick={() => setMobileFiltersOpen(value => !value)} aria-expanded={mobileFiltersOpen} aria-controls="mobile-document-filters">
                    <SlidersHorizontal className="h-4 w-4" />Filters{activeFilterCount > 0 && ` (${activeFilterCount})`}
                  </Button>
                  {folderControls}
                </div>
                {mobileFiltersOpen && <div id="mobile-document-filters" className="grid grid-cols-2 gap-2 [&>*:last-child]:col-span-2">{documentFilters}</div>}
              </div>
            ) : (
            <DataTableToolbar
              searchValue={searchQuery}
              onSearchChange={setSearchQuery}
              searchPlaceholder="Search documents..."
              searchTestId="search-input"
              className="border-0 bg-transparent p-0 [&>div]:items-center [&>div]:gap-2 [&>div>div:first-child]:lg:max-w-lg [&>div>div:last-child]:gap-2 [&>div>div:last-child]:sm:flex-nowrap"
            >
              {documentFilters}
              {folderControls}
            </DataTableToolbar>
            )}
          </div>
          <CardContent className="p-0">
            {compactDocuments ? (
              <>
                <div className="flex items-center justify-between gap-2 border-b border-border bg-muted/30 px-3 py-2">
                  <label className="flex min-h-11 cursor-pointer items-center gap-2 text-sm" htmlFor="select-all-documents">
                    <Checkbox id="select-all-documents" checked={allFilteredSelected ? true : someFilteredSelected ? 'indeterminate' : false} onCheckedChange={handleSelectAll} aria-label={allFilteredSelected ? 'Deselect all documents' : 'Select all documents'} data-testid="select-all" />
                    Select all
                  </label>
                  <Select value={`${sortBy}:${sortOrder}`} onValueChange={value => { const [key, direction] = value.split(':'); setSortBy(key); setSortOrder(direction); }}>
                    <SelectTrigger className="h-11 w-auto max-w-[60%] gap-2" aria-label="Sort documents"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="updated_at:desc">Recent updates</SelectItem>
                      <SelectItem value="updated_at:asc">Oldest updates</SelectItem>
                      <SelectItem value="title:asc">Name A to Z</SelectItem>
                      <SelectItem value="title:desc">Name Z to A</SelectItem>
                      <SelectItem value="topic:asc">Topic A to Z</SelectItem>
                      <SelectItem value="topic:desc">Topic Z to A</SelectItem>
                      <SelectItem value="status:asc">Status A to Z</SelectItem>
                      <SelectItem value="status:desc">Status Z to A</SelectItem>
                      <SelectItem value="region:asc">Location A to Z</SelectItem>
                      <SelectItem value="region:desc">Location Z to A</SelectItem>
                      <SelectItem value="groups:asc">Groups A to Z</SelectItem>
                      <SelectItem value="groups:desc">Groups Z to A</SelectItem>
                      <SelectItem value="ingestion_status:asc">Ingestion A to Z</SelectItem>
                      <SelectItem value="ingestion_status:desc">Ingestion Z to A</SelectItem>
                      {showPerformance && <><SelectItem value="retrieval_count:desc">Most retrievals</SelectItem><SelectItem value="retrieval_count:asc">Fewest retrievals</SelectItem><SelectItem value="helpful_rate:desc">Most helpful</SelectItem><SelectItem value="helpful_rate:asc">Least helpful</SelectItem></>}
                    </SelectContent>
                  </Select>
                </div>
                {loading ? <div className="space-y-3 p-4" data-testid="kb-documents-loading"><Skeleton className="h-5 w-3/4" /><Skeleton className="h-4 w-1/2" /></div> : filteredDocs.length === 0 ? <DocumentEmptyState activeView={activeView} canUpload={canUpload} onUpload={() => setUploadOpen(true)} /> : <ul aria-label="Documents in this view" className="min-w-0">{documentRows}</ul>}
              </>
            ) : (
            <div className="overflow-x-auto">
                <table className="w-full min-w-[1020px] table-fixed data-table">
                <thead className="bg-secondary border-b border-border">
                  <tr>
                    <th className="w-[34%] p-3 sm:p-4">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={allFilteredSelected ? true : someFilteredSelected ? 'indeterminate' : false}
                          onCheckedChange={handleSelectAll}
                          className="data-[state=indeterminate]:bg-primary/60 data-[state=indeterminate]:text-primary-foreground"
                          aria-label={allFilteredSelected ? 'Deselect all documents' : 'Select all documents'}
                          data-testid="select-all"
                        />
                        <DataTableSortHeader
                          label="Document"
                          sortKey="title"
                          activeSortKey={sortBy}
                          sortDirection={sortOrder}
                          onSort={updateSort}
                        />
                      </div>
                    </th>
                    <th className="hidden w-[12%] p-3 sm:p-4 md:table-cell">
                      <DataTableSortHeader
                        label="Topic"
                        sortKey="topic"
                        activeSortKey={sortBy}
                        sortDirection={sortOrder}
                        onSort={updateSort}
                      />
                    </th>
                    <th className="hidden w-[13%] p-3 sm:p-4 lg:table-cell">
                      <DataTableSortHeader
                        label="Location"
                        sortKey="region"
                        activeSortKey={sortBy}
                        sortDirection={sortOrder}
                        onSort={updateSort}
                      />
                    </th>
                    <th className="hidden w-[12%] p-3 sm:p-4 lg:table-cell">
                      <DataTableSortHeader
                        label="Groups"
                        sortKey="groups"
                        activeSortKey={sortBy}
                        sortDirection={sortOrder}
                        onSort={updateSort}
                      />
                    </th>
                    <th className="w-[10%] p-3 sm:p-4">
                      <DataTableSortHeader
                        label="Status"
                        sortKey="status"
                        activeSortKey={sortBy}
                        sortDirection={sortOrder}
                        onSort={updateSort}
                      />
                    </th>
                    {showPerformance && (
                      <>
                        <th className="hidden w-[13%] p-3 sm:p-4 xl:table-cell">
                          <DataTableSortHeader
                            label="Retrievals"
                            sortKey="retrieval_count"
                            activeSortKey={sortBy}
                            sortDirection={sortOrder}
                            onSort={updateSort}
                          />
                        </th>
                        <th className="hidden w-[11%] p-3 sm:p-4 xl:table-cell">
                          <DataTableSortHeader
                            label="Helpful"
                            sortKey="helpful_rate"
                            activeSortKey={sortBy}
                            sortDirection={sortOrder}
                            onSort={updateSort}
                          />
                        </th>
                      </>
                    )}
                    <th className="hidden w-[11%] p-3 sm:p-4 lg:table-cell">
                      <DataTableSortHeader
                        label="Ingestion"
                        sortKey="ingestion_status"
                        activeSortKey={sortBy}
                        sortDirection={sortOrder}
                        onSort={updateSort}
                      />
                    </th>
                    <th className="w-[96px] p-3 text-right sm:p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <tr key={index} data-testid="kb-documents-loading">
                        <td colSpan={showPerformance ? 11 : 9} className="p-3 sm:p-4">
                          <div className="flex items-center gap-3">
                            <Skeleton className="h-10 w-10 rounded-xl" />
                            <div className="flex-1 space-y-2">
                              <Skeleton className="h-4 w-56 max-w-full" />
                              <Skeleton className="h-3 w-40 max-w-full" />
                            </div>
                            <Skeleton className="hidden h-6 w-20 rounded-full md:block" />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : filteredDocs.length === 0 ? (
                    <tr>
                      <td colSpan={showPerformance ? 11 : 9}>
                        <DocumentEmptyState
                          activeView={activeView}
                          canUpload={canUpload}
                          onUpload={() => setUploadOpen(true)}
                        />
                      </td>
                    </tr>
                  ) : (
                    documentRows
                  )}
                </tbody>
              </table>
            </div>
            )}
          </CardContent>
          
          {/* Pagination Controls */}
          {allFilteredDocs.length > 0 && (
            compactDocuments ? <div className="space-y-2 border-t border-border p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-muted-foreground">{(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, allFilteredDocs.length)} of {allFilteredDocs.length} document{allFilteredDocs.length !== 1 && 's'}</p>
                <Select value={String(pageSize)} onValueChange={value => setPageSize(Number(value))}><SelectTrigger className="h-11 w-28" aria-label="Documents per page" data-testid="kb-page-size"><SelectValue /></SelectTrigger><SelectContent>{[10,25,50].map(size => <SelectItem key={size} value={String(size)}>{size} / page</SelectItem>)}</SelectContent></Select>
              </div>
              {totalPages > 1 && <div className="flex items-center justify-between gap-2"><Button variant="outline" className="h-11" disabled={currentPage <= 1} onClick={() => setCurrentPage(page => page - 1)}>Previous</Button><span className="text-sm text-muted-foreground">Page {currentPage} of {totalPages}</span><Button variant="outline" className="h-11" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(page => page + 1)}>Next</Button></div>}
            </div> : (
            <DataTablePagination
              page={currentPage}
              totalPages={totalPages}
              totalItems={allFilteredDocs.length}
              pageSize={pageSize}
              pageSizeOptions={[10, 25, 50]}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              itemLabel="documents"
              pageSizeTestId="kb-page-size"
              className="bg-muted px-4 py-3"
            />
            )
          )}
        </Card>
      )}
      </div>{/* End of Main Content */}

      <Sheet open={folderPanelOpen} onOpenChange={setFolderPanelOpen}>
        <SheetContent side="left" className="w-full max-w-md p-0 sm:max-w-md [&>button]:h-11 [&>button]:w-11 [&>button]:top-1 [&>button]:right-1 [&>button]:flex [&>button]:items-center [&>button]:justify-center">
          <div className="flex h-full flex-col">
            <SheetHeader className="border-b border-border px-4 py-4">
              <SheetTitle className="flex items-center gap-2">
                <FolderOpen className="h-5 w-5 text-primary" />
                Folders
              </SheetTitle>
              <SheetDescription>
                Select a folder to filter the document table.
              </SheetDescription>
            </SheetHeader>
            <div className="min-h-0 flex-1 overflow-hidden">
              <FolderTree
                ref={folderTreeRef}
                selectedFolderId={selectedFolderId}
                onSelectFolder={(folderId) => {
                  setSelectedFolderId(folderId);
                  setFolderPanelOpen(false);
                }}
                canEdit={canUpload}
                canDelete={canDelete}
                onChange={({ type, id }) => {
                  setFolderRevision(value => value + 1);
                  if (type === 'delete' && id === selectedFolderId) setSelectedFolderId(null);
                }}
              />
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Dialogs */}
      <UploadDocumentDialog open={uploadOpen} onOpenChange={setUploadOpen} onSuccess={handleUploadSuccess} topics={topics} folderId={selectedFolderId} canManagePlan={canManagePlan} onCloseAutoFocus={restoreUploadFocus} />
      <BulkUploadDialog open={bulkUploadOpen} onOpenChange={setBulkUploadOpen} onSuccess={handleUploadSuccess} currentFolderId={selectedFolderId} destinationLabel={selectedFolderId && selectedFolderId !== 'unfiled' ? visibleFolderPath.map(f => f.name).join(' / ') : 'Unfiled'} canManagePlan={canManagePlan} onCloseAutoFocus={restoreUploadFocus} />
      <DocumentPreviewDialog doc={previewDoc} open={!!previewDoc} onOpenChange={(v) => !v && setPreviewDoc(null)} />
      <DocumentDetailsDialog 
        doc={detailsDoc} 
        details={docDetails} 
        loading={detailsLoading}
        error={detailsError}
        onRetry={() => handleAction('details', detailsDoc, { returnFocus: detailsReturnFocusRef.current })}
        open={!!detailsDoc} 
        onOpenChange={(v) => { if (!v) { detailsRequestRef.current += 1; setDetailsDoc(null); setDocDetails(null); setDetailsError(''); } }}
        onCloseAutoFocus={(event) => {
          const target = detailsReturnFocusRef.current?.isConnected ? detailsReturnFocusRef.current :
            [...document.querySelectorAll('[data-testid]')].find(el => el.dataset.testid === `select-doc-${detailsReturnIdRef.current}`) || document.querySelector('[data-testid="refresh-table"]');
          if (target) {
            event.preventDefault();
            target.focus();
          }
        }}
        topicsMap={topicsMap}
        onAction={handleAction}
        canPublish={canPublish}
        canApprove={canApprove}
        canUpload={canUpload}
        canDelete={canDelete}
        canManagePlan={canManagePlan}
        topics={topics}
      />
      <FolderMoveDialog
        open={moveToFolderOpen} 
        onOpenChange={setMoveToFolderOpen} 
        documents={docsToMove}
        onSuccess={() => { fetchDocuments(); setSelectedDocs(new Set()); }}
      />
      
      {metadataDocuments && <BulkMetadataDialog documents={metadataDocuments} isAdmin={hasRole(['admin'])}
        onClose={() => { setMetadataDocuments(null); fetchDocuments({ tableOnly: true }); }}
        onUpdated={ids => { setSelectedDocs(prev => new Set([...prev].filter(id => !ids.includes(id)))); fetchDocuments({ tableOnly: true }); }} />}
      {/* Bulk Action Dialog */}
      <Dialog open={bulkActionOpen} onOpenChange={setBulkActionOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {bulkActionType === 'delete' && <Trash2 className="w-5 h-5 text-destructive" />}
              {bulkActionType === 'metadata' && <Edit className="w-5 h-5 text-primary" />}
              {bulkActionType === 'approve' && <CheckCircle className="w-5 h-5 text-success" />}
              {bulkActionType === 'reject' && <XCircle className="w-5 h-5 text-warning" />}
              {bulkActionType === 'submit_for_review' && <Send className="w-5 h-5 text-info" />}
              {bulkActionType === 'archive' && <Archive className="w-5 h-5 text-muted-foreground" />}
              Bulk {bulkActionType?.replace(/_/g, ' ')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-muted-foreground mb-4">
              This action will affect <span className="font-semibold text-primary">{selectedDocs.size}</span> document{selectedDocs.size > 1 ? 's' : ''}.
            </p>
            
            {/* Status-aware warning */}
            {bulkActionType && !isBulkActionValidForSelection(bulkActionType) && bulkActionType !== 'metadata' && (
              <div className="bg-warning/10 border border-warning/30 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-warning">Some documents cannot be updated</p>
                    <p className="text-xs text-warning mt-1">
                      {getInvalidDocsCount(bulkActionType)} document(s) have an incompatible status. They will be skipped.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {bulkActionType === 'delete' && (
              <p className="text-destructive text-sm">
                Warning: This action cannot be undone. All selected documents will be permanently deleted.
              </p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkActionOpen(false)} disabled={bulkActionLoading}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkAction} 
              disabled={bulkActionLoading}
              className={bulkActionType === 'delete' ? 'bg-destructive hover:bg-destructive/90' : 'bg-primary text-primary-foreground hover:bg-primary/90'}
            >
              {bulkActionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Floating Action Button - removed */}
    </div>
  );
}
